/* eslint jsx-a11y/label-has-associated-control: 0 */
/* eslint jsx-a11y/label-has-for: 0 */

import React, { ReactElement } from 'react';
import { i18n } from 'config/locales';
import ConnectedAlternateAddress from 'components/Form/Location/AlternateAddress';
import {
  isCitizen,
  requireCitizenshipDocumentation,
} from 'forms/sections/relationships/relatives/validators/item';
import { RelationshipsRelativeItemModel } from 'forms/sections/relationships/relatives/types';
import {
  CommonAddressBooks,
  CommonScrollIntoView,
  CommonRequired,
  CommonOnError,
  CommonOnUpdate,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { YesOrNoModel, DateModel, CheckboxModel } from 'forms/shared/types';
import { Dispatch } from 'redux';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import NotApplicable from 'components/Form/NotApplicable';
import Textarea from 'components/Form/Textarea';
import Name from 'components/Form/Name';
import DateControl from 'components/Form/DateControl';
import Country from 'components/Form/Country';
import Branch from 'components/Form/Branch';
import BranchCollection from 'components/Form/BranchCollection';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Location from 'components/Form/Location';
import Layout from 'components/Form/PVQLocation/Layouts';
import Text from 'components/Form/Text';
import {
  immediateFamilyOptions,
  immediateAdditionalFamilyOptions,
  relativeTypes,
} from 'constants/enums/relationshipOptions';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import PVQName from 'components/Form/PVQName';
import PVQLocation from 'components/Form/PVQLocation';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import {
  ApplicableModel, LocationModel
} from 'forms/shared/types';
import {
  PVQ_CITIZEN, PVQ_DERIVED, PVQ_FOREIGN_BORN, PVQ_NATIONAL_BY_BIRTH_IN_US, PVQ_NATIONAL_BY_BIRTH_OUTSIDE_US, PVQ_NATURALIZED, PVQ_NOT_CITIZEN, isNationalOrNonCitizen,
  isNotUSCLivesInUS
} from 'constants/enums/citizenshipOptions';
import {
  DOC_DerivedCertificate, DOC_DerivedMriv, DOC_DerivedPermanentAlien, DOC_DS1350, DOC_FS240, DOC_FS545, DOC_N560, DOC_NaturalizedCertificate, DOC_NaturalizedMriv, DOC_NaturalizedPermanentAlien, DOC_NonArrival, DOC_NonEmployment, DOC_NonF1, DOC_NonJ1, DOC_NonMRIV, DOC_NonPermanent, DOC_NonVisa, DOC_Other, RES_Other, RES_ResidencyAsylee, RES_ResidencyDACA, RES_ResidencyNonimmigrant, RES_ResidencyPermanent, RES_ResidencyTPS
} from 'constants/enums/foreignBornDocumentCivlTypes';
import { getCountryValue } from 'helpers/locations';
import { countryString } from '../../../../validators/location';
import { pickDate, alphaNumericRegEx } from '../../../../validators/helpers';
import Dropdown from '../../../Form/Dropdown/Dropdown';
import Alias from './Alias';
import RelativeAffiliation from './RelativeAffiliation';

type UpdateField<Model extends {}> = <Key extends keyof Model>(
  field: Key,
  values: Model[Key]
) => void;

interface Props extends Partial<RelationshipsRelativeItemModel>, AccordionItemProps {
  applicantBirthdate: DateModel;
  scrollIntoView: CommonScrollIntoView;
  required: CommonRequired;
  onError: CommonOnError;
  onUpdate: CommonOnUpdate;
  onValidate: any;
  addressBooks: CommonAddressBooks;
  addressBook: any;
  dispatch: Dispatch;
  country: any;
  HasAffiliation: YesOrNoModel;
  requireRelationshipRelativesForeignBornDoc: boolean;
  requireRelationshipRelativesUSResidenceDoc: boolean;
  requireRelationshipRelativesForeignGovtAffExplanation: boolean;
  requireRelationshipRelativesAdditionalFamily: boolean;
  requireRelationshipRelativesAPOFPOIDK: boolean;
  Relation: any;
  Citizenship: any;
  IsDeceased: any;
  value: any;
  applicable: any;
  MaidenSameAsListed: YesOrNoModel;
  PvqRelation?: any; // pvq only
  formType?: string; // pvq only
}

export default class Relative extends ValidationElement<Props> {
  handleNameError = (value: RelationshipsRelativeItemModel['Name'], arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  getCitizenshipDocumentation = () => [
    {
      name: 'derived-alien',
      label: i18n.m('relationships.relatives.label.derived.alien'),
      value: 'DerivedAlien',
    },
    {
      name: 'derived-permanent',
      label: i18n.m('relationships.relatives.label.derived.permanent'),
      value: 'DerivedPermanent',
    },
    {
      name: 'derived-certificate',
      label: i18n.m('relationships.relatives.label.derived.certificate'),
      value: 'DerivedCertificate',
    },
    {
      name: 'derived-other',
      label: i18n.m('relationships.relatives.label.derived.other'),
      value: 'Other',
    },
  ];

  getNonCitizenDocumentation = () => [
    {
      name: 'document-permanent',
      label: i18n.m('relationships.relatives.label.document.permanent'),
      value: 'Permanent',
    },
    {
      name: 'document-employment',
      label: i18n.m('relationships.relatives.label.document.employment'),
      value: 'Employment',
    },
    {
      name: 'document-arrival',
      label: i18n.m('relationships.relatives.label.document.arrival'),
      value: 'Arrival',
    },
    {
      name: 'document-visa',
      label: i18n.m('relationships.relatives.label.document.visa'),
      value: 'Visa',
    },
    {
      name: 'document-f1',
      label: i18n.m('relationships.relatives.label.document.f1'),
      value: 'F1',
    },
    {
      name: 'document-j1',
      label: i18n.m('relationships.relatives.label.document.j1'),
      value: 'J1',
    },
    {
      name: 'document-other',
      label: i18n.m('relationships.relatives.label.document.other'),
      value: 'Other',
    },
  ];

  getPvqCitizenshipStatus = () => [
    {
      name: 'us-citizen',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.usCitizen'),
      value: PVQ_CITIZEN,
    },
    {
      name: 'us-foreign',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.usForeign'),
      value: PVQ_FOREIGN_BORN,
    },
    {
      name: 'naturalized',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.naturalized'),
      value: PVQ_NATURALIZED,
    },
    {
      name: 'derived',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.derived'),
      value: PVQ_DERIVED,
    },
    {
      name: 'not-us',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.notUs'),
      value: PVQ_NOT_CITIZEN,
    },
    {
      name: 'national-by-birth-in-us',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.nationalByBirthInUs'),
      value: PVQ_NATIONAL_BY_BIRTH_IN_US,
    },
    {
      name: 'national-by-birth-outside-us',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.nationalByBirthOutsideUS'),
      value: PVQ_NATIONAL_BY_BIRTH_OUTSIDE_US,
    },
  ];

  getPvqUsForeignDocumentation = () => [
    {
      name: 'us-foreign-fs240',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.fs240'),
      value: DOC_FS240,
    },
    {
      name: 'us-foreign-ds1350',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.ds1350'),
      value: DOC_DS1350,
    },
    {
      name: 'us-foreign-fs545',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.fs545'),
      value: DOC_FS545,
    },
    {
      name: 'us-foreign-n560',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.n560'),
      value: DOC_N560,
    },
    {
      name: 'us-foreign-other',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.other'),
      value: DOC_Other,
    }
  ];

  getPvqNaturalizedDocumentation = () => [
    {
      name: 'naturalized-certificate',
      label: i18n.m('relationships.pvq.relatives.label.naturalized.certificate'),
      value: DOC_NaturalizedCertificate,
    },
    {
      name: 'naturalized-permanent-alien',
      label: i18n.m('relationships.pvq.relatives.label.naturalized.permanentAlien'),
      value: DOC_NaturalizedPermanentAlien,
    },
    {
      name: 'naturalized-mriv',
      label: i18n.m('relationships.pvq.relatives.label.naturalized.mriv'),
      value: DOC_NaturalizedMriv,
    },
    {
      name: 'naturalized-other',
      label: i18n.m('relationships.pvq.relatives.label.naturalized.other'),
      value: DOC_Other,
    },
  ];

  getPvqDerivedDocumentation = () => [
    {
      name: 'derived-certificate',
      label: i18n.m('relationships.pvq.relatives.label.derived.certificate'),
      value: DOC_DerivedCertificate,
    },
    {
      name: 'derived-permanent-alien',
      label: i18n.m('relationships.pvq.relatives.label.derived.permanentAlien'),
      value: DOC_DerivedPermanentAlien,
    },
    {
      name: 'derived-mriv',
      label: i18n.m('relationships.pvq.relatives.label.derived.mriv'),
      value: DOC_DerivedMriv,
    },
    {
      name: 'derived-other',
      label: i18n.m('relationships.pvq.relatives.label.derived.other'),
      value: DOC_Other,
    },
  ];

  getPvqResidency = () => [
    {
      name: 'residency-permanent',
      label: i18n.m('relationships.pvq.relatives.label.residency.permanent'),
      value: RES_ResidencyPermanent,
    },
    {
      name: 'residency-permanent-alien',
      label: i18n.m('relationships.pvq.relatives.label.residency.asylee'),
      value: RES_ResidencyAsylee,
    },
    {
      name: 'residency-mriv',
      label: i18n.m('relationships.pvq.relatives.label.residency.nonimmigrant'),
      value: RES_ResidencyNonimmigrant,
    },
    {
      name: 'residency-tps',
      label: i18n.m('relationships.pvq.relatives.label.residency.tps'),
      value: RES_ResidencyTPS,
    },
    {
      name: 'residency-daca',
      label: i18n.m('relationships.pvq.relatives.label.residency.daca'),
      value: RES_ResidencyDACA,
    },
    {
      name: 'residency-other',
      label: i18n.m('relationships.pvq.relatives.label.residency.other'),
      value: RES_Other,
    },
  ];

  getPvqNonCitizenDocumentation = () => [
    {
      name: 'document-permanent',
      label: i18n.m('relationships.pvq.relatives.label.document.permanent'),
      value: DOC_NonPermanent,
    },
    {
      name: 'document-employment',
      label: i18n.m('relationships.pvq.relatives.label.document.employment'),
      value: DOC_NonEmployment,
    },
    {
      name: 'document-mriv',
      label: i18n.m('relationships.pvq.relatives.label.document.mriv'),
      value: DOC_NonMRIV,
    },
    {
      name: 'document-arrival',
      label: i18n.m('relationships.pvq.relatives.label.document.arrival'),
      value: DOC_NonArrival,
    },
    {
      name: 'document-visa',
      label: i18n.m('relationships.pvq.relatives.label.document.visa'),
      value: DOC_NonVisa,
    },
    {
      name: 'document-f1',
      label: i18n.m('relationships.pvq.relatives.label.document.f1'),
      value: DOC_NonF1,
    },
    {
      name: 'document-j1',
      label: i18n.m('relationships.pvq.relatives.label.document.j1'),
      value: DOC_NonJ1,
    },
    {
      name: 'document-other',
      label: i18n.m('relationships.pvq.relatives.label.document.other'),
      value: DOC_Other,
    },
  ];

  getPvqEmployerRelationship = () => [
    {
      name: 'military-armed',
      label: i18n.m('relationships.pvq.relatives.label.association.military'),
      value: 'Military',
    },
    {
      name: 'security-service',
      label: i18n.m('relationships.pvq.relatives.label.association.security'),
      value: 'Security',
    },
    {
      name: 'intelligence-service',
      label: i18n.m('relationships.pvq.relatives.label.association.intelligence'),
      value: 'Intelligence',
    },
    {
      name: 'government-agency',
      label: i18n.m('relationships.pvq.relatives.label.association.government'),
      value: 'Government',
    },
    {
      name: 'defense-industry',
      label: i18n.m('relationships.pvq.relatives.label.association.defense'),
      value: 'Defense',
    },
    {
      name: 'foreign-movement',
      label: i18n.m('relationships.pvq.relatives.label.association.foreign'),
      value: 'Foreign',
    },
    {
      name: 'not-associated',
      label: i18n.m('relationships.pvq.relatives.label.association.not'),
      value: 'Not Associated',
    },
  ];

  getFrequency = () => [
    {
      name: 'frequency-daily',
      label: i18n.m('relationships.relatives.label.frequency.daily'),
      value: 'Daily',
    },
    {
      name: 'frequency-weekly',
      label: i18n.m('relationships.relatives.label.frequency.weekly'),
      value: 'Weekly',
    },
    {
      name: 'frequency-monthly',
      label: i18n.m('relationships.relatives.label.frequency.monthly'),
      value: 'Monthly',
    },
    {
      name: 'frequency-quarterly',
      label: i18n.m('relationships.relatives.label.frequency.quarterly'),
      value: 'Quarterly',
      className: 'frequency-quarterly',
    },
    {
      name: 'frequency-annually',
      label: i18n.m('relationships.relatives.label.frequency.annually'),
      value: 'Annually',
    },
    {
      name: 'frequency-other',
      label: i18n.m('relationships.relatives.label.frequency.other'),
      value: 'Other',
    },
  ];

  // this includes all relatives except parents and guardian
  getOtherRelativeOptions = () => [
    {
      name: 'relation-stepmother',
      label: i18n.t('relationships.relatives.label.relation.stepmother'),
      value: 'Stepmother',
    },
    {
      name: 'relation-stepfather',
      label: i18n.t('relationships.relatives.label.relation.stepfather'),
      value: 'Stepfather',
    },
    {
      name: 'relation-fosterparent',
      label: i18n.t('relationships.relatives.label.relation.fosterparent'),
      value: 'Fosterparent',
    },
    {
      name: 'relation-child',
      label: i18n.t('relationships.relatives.label.relation.child'),
      value: 'Child',
    },
    {
      name: 'relation-stepchild',
      label: i18n.t('relationships.relatives.label.relation.stepchild'),
      value: 'Stepchild',
    },
    {
      name: 'relation-brother',
      label: i18n.t('relationships.relatives.label.relation.brother'),
      value: 'Brother',
    },
    {
      name: 'relation-sister',
      label: i18n.t('relationships.relatives.label.relation.sister'),
      value: 'Sister',
    },
    {
      name: 'relation-stepbrother',
      label: i18n.t('relationships.relatives.label.relation.stepbrother'),
      value: 'Stepbrother',
    },
    {
      name: 'relation-stepsister',
      label: i18n.t('relationships.relatives.label.relation.stepsister'),
      value: 'Stepsister',
    },
    {
      name: 'relation-halfbrother',
      label: i18n.t('relationships.relatives.label.relation.halfbrother'),
      value: 'Half-brother',
    },
    {
      name: 'relation-halfsister',
      label: i18n.t('relationships.relatives.label.relation.halfsister'),
      value: 'Half-sister',
    },
  ];

  // this includes all Pvq relatives
  getPvqRelativeOptions = () => [
    {
      name: 'relation-parent',
      label: i18n.t('relationships.pvq.relatives.label.relation.parent'),
      value: 'Parent',
    },
    {
      name: 'relation-stepparent',
      label: i18n.t('relationships.pvq.relatives.label.relation.stepparent'),
      value: 'Stepparent',
    },
    {
      name: 'relation-pvq-fosterparent',
      label: i18n.t('relationships.pvq.relatives.label.relation.fosterparent'),
      value: 'Fosterparent',
    },
    {
      name: 'relation-pvq-child',
      label: i18n.t('relationships.pvq.relatives.label.relation.child'),
      value: 'Child',
    },
    {
      name: 'relation-pvq-stepchild',
      label: i18n.t('relationships.pvq.relatives.label.relation.stepchild'),
      value: 'Stepchild',
    },
    {
      name: 'relation-sibling',
      label: i18n.t('relationships.pvq.relatives.label.relation.sibling'),
      value: 'Sibling',
    },
    {
      name: 'relation-stepsibling',
      label: i18n.t('relationships.pvq.relatives.label.relation.stepsibling'),
      value: 'Stepsibling',
    },
    {
      name: 'relation-halfsibling',
      label: i18n.t('relationships.pvq.relatives.label.relation.halfsibling'),
      value: 'Halfsibling',
    },
    {
      name: 'relation-parentinlaw',
      label: i18n.t('relationships.pvq.relatives.label.relation.parentinlaw'),
      value: 'Parent-in-law',
    },
    {
      name: 'relation-pvq-guardian',
      label: i18n.t('relationships.pvq.relatives.label.relation.guardian'),
      value: 'Guardian',
    },
  ];

  getDnkRelativePrefills = () => ({
    name: {
      first: i18n.t('relationships.relatives.prefill.name.first'),
      middle: i18n.t('relationships.relatives.prefill.name.middle'),
      last: i18n.t('relationships.relatives.prefill.name.last'),
    },
    birthdate: {
      day: i18n.t('relationships.relatives.prefill.birthdate.day'),
      month: i18n.t('relationships.relatives.prefill.birthdate.month'),
      year: i18n.t('relationships.relatives.prefill.birthdate.year'),
      estimated: false,
      touched: true,
    },
    birthplace: {
      city: i18n.t('relationships.relatives.prefill.birthplace.city'),
      state: i18n.t('relationships.relatives.prefill.birthplace.state'),
      country: {
        value: i18n.t('relationships.relatives.prefill.birthplace.country.value'),
      },
    },
    citizenship: {
      name: i18n.t('relationships.relatives.prefill.citizenship.name'),
      showComments: false,
      value: i18n.t('relationships.relatives.prefill.citizenship.value'),
    },
    deceased: {
      name: i18n.t('relationships.relatives.prefill.deceased.name'),
      value: i18n.t('relationships.relatives.prefill.deceased.value'),
    },
    aliases: {
      items: [
        {
          Item: {
            Has: {
              name: i18n.t('relationships.relatives.prefill.aliases.name'),
              value: i18n.t('relationships.relatives.prefill.aliases.value'),
            },
          },
        },
      ],
    },
    maidenSameAsListed: {
      name: i18n.t('relationships.relatives.prefill.maidenSameAsListed.name'),
      value: i18n.t('relationships.relatives.prefill.maidenSameAsListed.value'),
    },
  });

  documentExpirationRequired = (value) =>
    // Expiration only required for the following:
    // NonCitizenI766 = Employment, NonCitizenI94 = Arrival, NonCitizenI20 = F1, NonCitizenDS2019 = J1
    ['Employment', 'Arrival', 'F1', 'J1'].includes(value);

  update = (queue: Partial<RelationshipsRelativeItemModel>) => {
    this.props.onUpdate({
      Relation: this.props.Relation,
      PvqRelation: this.props.PvqRelation, // pvq only
      NoContactWithFamily: this.props.NoContactWithFamily, // pvq only
      PvqEmployerRelationship: this.props.PvqEmployerRelationship, // pvq only
      PvqOtherGovtAgencyExplanation: this.props.PvqOtherGovtAgencyExplanation, // pvq only
      CitizenshipStatus: this.props.CitizenshipStatus, // pvq only
      Residency: this.props.Residency, // pvq only
      AlienNumber: this.props.AlienNumber, // pvq only
      CertificateNumber: this.props.CertificateNumber, // pvq only
      InContact: this.props.InContact, // pvq only
      RelativeOccupation: this.props.RelativeOccupation, // pvq only
      BirthdateNotApplicable: this.props.BirthdateNotApplicable, // pvq only
      BirthplaceNotApplicable: this.props.BirthplaceNotApplicable, // pvq only
      DeceasedNotApplicable: this.props.DeceasedNotApplicable, // pvq only
      RelativeRelationshipNotApplicable: this.props.RelativeRelationshipNotApplicable, // pvq only
      AffiliationNotApplicable: this.props.AffiliationNotApplicable, // pvq only
      HasRelativeAffiliations: this.props.HasRelativeAffiliations, // pvq only
      Name: this.props.Name,
      Birthdate: this.props.Birthdate,
      Birthplace: this.props.Birthplace,
      Citizenship: this.props.Citizenship,
      MaidenSameAsListed: this.props.MaidenSameAsListed,
      MaidenName: this.props.MaidenName,
      Aliases: this.props.Aliases,
      IsDeceased: this.props.IsDeceased,
      Address: this.props.Address,
      CitizenshipDocumentation: this.props.CitizenshipDocumentation,
      OtherCitizenshipDocumentation: this.props.OtherCitizenshipDocumentation,
      DocumentNumber: this.props.DocumentNumber,
      CertificateNumberNotApplicable: this.props.CertificateNumberNotApplicable,
      CourtName: this.props.CourtName,
      CourtNameNotApplicable: this.props.CourtNameNotApplicable,
      CourtAddress: this.props.CourtAddress,
      Document: this.props.Document,
      DocumentComments: this.props.DocumentComments,
      OptionalComments: this.props.OptionalComments,
      ResidenceDocumentNumber: this.props.ResidenceDocumentNumber,
      DocumentNumberNotApplicable: this.props.DocumentNumberNotApplicable,
      DocumentNumberExplanation: this.props.DocumentNumberExplanation,
      Expiration: this.props.Expiration,
      ExpirationUnknown: this.props.ExpirationUnknown,
      ExpirationNotApplicable: this.props.ExpirationNotApplicable,
      FirstContact: this.props.FirstContact,
      LastContact: this.props.LastContact,
      Methods: this.props.Methods,
      MethodsComments: this.props.MethodsComments,
      Frequency: this.props.Frequency,
      FrequencyExplanation: this.props.FrequencyExplanation,
      FrequencyComments: this.props.FrequencyComments,
      EmployerNotApplicable: this.props.EmployerNotApplicable,
      EmployerAddressNotApplicable: this.props.EmployerAddressNotApplicable,
      EmployerRelationshipNotApplicable: this.props.EmployerRelationshipNotApplicable,
      Employer: this.props.Employer,
      EmployerAddress: this.props.EmployerAddress,
      HasAffiliation: this.props.HasAffiliation,
      EmployerRelationship: this.props.EmployerRelationship,
      AlternateAddress: this.props.AlternateAddress,
      AlternateAddressNotApplicable: this.props.AlternateAddressNotApplicable,
      EntryComments: this.props.EntryComments,
      EntryExplanation: this.props.EntryExplanation,
      ParentNotApplicable: this.props.ParentNotApplicable,
      MothersMaidenNameComments: this.props.MothersMaidenNameComments,
      AliasesComments: this.props.AliasesComments,
      AliasesSummaryComments: this.props.AliasesSummaryComments,
      DeceasedComments: this.props.DeceasedComments,
      ForeignRelativeComments: this.props.ForeignRelativeComments,
      EmployerComments: this.props.EmployerComments,
      HasAffiliationComments: this.props.HasAffiliationComments,
      ForeignAffiliationComments: this.props.ForeignAffiliationComments,
      AnswerComments: this.props.AnswerComments,
      AddressComments: this.props.AddressComments,
      PhysicalLocationComments: this.props.PhysicalLocationComments,
      AlternateAddressExplanation: this.props.AlternateAddressExplanation,
      AddressNotApplicable: this.props.AddressNotApplicable,
      AddressExplanation: this.props.AddressExplanation,
      CourtAddressNotApplicable: this.props.CourtAddressNotApplicable,
      CourtAddressExplanation: this.props.CourtAddressExplanation,
      FirstContactNotApplicable: this.props.FirstContactNotApplicable,
      FirstContactExplanation: this.props.FirstContactExplanation,
      InUsaNotApplicable: this.props.InUsaNotApplicable,
      CitizenshipCountryNotApplicable: this.props.CitizenshipCountryNotApplicable,
      ParentCitizenshipStatusNotApplicable: this.props.ParentCitizenshipStatusNotApplicable,
      NeverWorked: this.props.NeverWorked, // PVQ Only
      ...queue,
    });
  };

  getDocumentValuesToOverwrite = (field: string) => (field === 'Citizenship'
    ? {
      CitizenshipDocumentation: { name: '', value: '', checked: false },
      Document: { name: '', value: '', checked: false },
    }
    : {});

  updateField: UpdateField<RelationshipsRelativeItemModel> = (field, values) => {
    const documentValues = this.getDocumentValuesToOverwrite(field);

    this.update({
      [field]: values,
      ...documentValues,
    });
  };

  updateNoContactWithFamily = (values: RelationshipsRelativeItemModel['NoContactWithFamily']) => {
    this.update({
      NoContactWithFamily: values
    });
  };

  updateInUsaNotApplicable = (
    inUsaNotApplicable: ApplicableModel
  ) => {
    const queue: Partial<RelationshipsRelativeItemModel> = {
      InUsaNotApplicable: { applicable: inUsaNotApplicable.applicable }
    };

    if (!inUsaNotApplicable.applicable) {
      queue.Address = {} as LocationModel;
    }

    this.update(queue);
  };

  updateDocument = (value: RelationshipsRelativeItemModel['Document']) => {
    const additionalValues = this.documentExpirationRequired(value.value) ? {} : { Expiration: {} };
    this.update({
      Document: value,
      CitizenshipDocumentation: {},
      ...additionalValues
    });
  };

  updateCourtName = (value: RelationshipsRelativeItemModel['CourtName']) => {
    this.update({
      CourtName: value,
    });
  };

  updateCourtNameNotApplicable = (values: RelationshipsRelativeItemModel['CourtNameNotApplicable']) => {
    this.update({
      CourtNameNotApplicable: values,
      CourtName: values?.applicable ? this.props.CourtName : {},
    });
  };

  updateCertificateNumberNotApplicable = (values: RelationshipsRelativeItemModel['CertificateNumberNotApplicable']) => {
    this.update({
      CertificateNumberNotApplicable: values,
      CertificateNumber: values?.applicable ? this.props.CertificateNumber : {},
    });
  };

  updateCourtAddress = (value: RelationshipsRelativeItemModel['CourtAddress']) => {
    this.update({
      CourtAddress: value,
    });
  };

  updateAlienNumber = (value: RelationshipsRelativeItemModel['AlienNumber']) => {
    this.update({
      AlienNumber: value,
    });
  };

  updateCertificateNumber = (value: RelationshipsRelativeItemModel['CertificateNumber']) => {
    this.update({
      CertificateNumber: value,
    });
  };

  updateAddressExplanation = (value: RelationshipsRelativeItemModel['AddressExplanation']) => {
    this.update({
      AddressExplanation: value,
    });
  };

  updateResidency = (value: RelationshipsRelativeItemModel['Residency']) => {
    this.update({
      Residency: value,
    });
  };

  updateOtherCitizenshipDocumentation = (value: RelationshipsRelativeItemModel['OtherCitizenshipDocumentation']) => {
    this.update({
      OtherCitizenshipDocumentation: value,
    });
  };

  updateFirstContactExplanation = (value: RelationshipsRelativeItemModel['FirstContactExplanation']) => {
    this.update({
      FirstContactExplanation: value,
    });
  };

  updateExpiration = (value: RelationshipsRelativeItemModel['Expiration']) => {
    this.update({
      Expiration: value,
    });
  };

  updateExpirationUnknown = (values: RelationshipsRelativeItemModel['ExpirationUnknown']) => {
    this.update({
      ExpirationUnknown: values,
      ExpirationNotApplicable: {},
      Expiration: values?.applicable ? this.props.Expiration : {},
    });
  };

  updateExpirationNotApplicable = (values: RelationshipsRelativeItemModel['ExpirationNotApplicable']) => {
    this.update({
      ExpirationNotApplicable: values,
      ExpirationUnknown: {},
      Expiration: values?.applicable ? this.props.Expiration : {},
    });
  };

  updateCitizenshipStatus = (value: RelationshipsRelativeItemModel['CitizenshipStatus']) => {
    this.update({
      CitizenshipStatus: value,
      CitizenshipDocumentation: {},
    });
  };

  updateCitizenshipDocumentation = (value: RelationshipsRelativeItemModel['CitizenshipDocumentation']) => {
    this.update({
      CitizenshipDocumentation: value,
      Document: {},
      DocumentNumber: {},
      DocumentNumberNotApplicable: { applicable: true },
      CertificateNumberNotApplicable: { applicable: true },
      CertificateNumber: {},
      CourtName: {},
      CourtAddress: {},
      OtherCitizenshipDocumentation: {},
    });
  };

  updateCitizenship = (value: RelationshipsRelativeItemModel['Citizenship']) => {
    const additionalValues = value?.value?.includes?.('United States') ? {
      FirstContactNotApplicable: { applicable: true },
      FirstContactExplanation: {}
    } : {
      CourtAddressNotApplicable: { applicable: true },
      CourtAddressExplanation: {},
    };
    this.update({
      Citizenship: value,
      ...additionalValues
    });
  };

  updateBirthPlacePVQ = (value: RelationshipsRelativeItemModel['Birthplace']) => {
    // need to clear document values and all associated to foreignborn when US is selected
    const additional = value?.country?.value === 'United States' ? {
      CitizenshipDocumentation: {},
      DocumentNumber: {},
      OtherCitizenshipDocumentation: {},
      CourtName: {},
      CourtAddress: {},
      OptionalComments: {},
      ForeignRelativeComments: {},
      Document: {},
      DocumentComments: {},
      ResidenceDocumentNumber: {},
      DocumentNumberExplanation: {},
      Expiration: {},
      FrequencyComments: {},
      Employer: {},
      EmployerNotApplicable: { applicable: true },
      EmployerAddress: {},
      EmployerAddressNotApplicable: { applicable: true },
      EmployerComments: {},
      HasAffiliation: {},
      HasAffiliationComments: {},
      EmployerRelationship: {},
      ForeignAffiliationComments: {},
      CourtAddressNotApplicable: { applicable: true },
      CourtAddressExplanation: {},
    } : {};
    this.update({
      Birthplace: value,
      ...additional
    });
  };

  updateBirthPlace = (value: RelationshipsRelativeItemModel['Birthplace']) => {
    // need to clear document values and all associated to foreignborn when US is selected
    const additional = value?.country?.value === 'United States' ? {
      CitizenshipDocumentation: {},
      DocumentNumber: {},
      OtherCitizenshipDocumentation: {},
      CourtName: {},
      CourtAddress: {},
      OptionalComments: {},
      ForeignRelativeComments: {},
      Document: {},
      DocumentComments: {},
      ResidenceDocumentNumber: {},
      DocumentNumberExplanation: {},
      Expiration: {},
      FirstContact: {},
      LastContact: {},
      MethodsComments: {},
      Methods: {},
      FrequencyComments: {},
      Employer: {},
      EmployerNotApplicable: { applicable: true },
      EmployerAddress: {},
      EmployerAddressNotApplicable: { applicable: true },
      EmployerComments: {},
      HasAffiliation: {},
      HasAffiliationComments: {},
      EmployerRelationship: {},
      ForeignAffiliationComments: {},
      CourtAddressNotApplicable: { applicable: true },
      CourtAddressExplanation: {},
    } : {};
    this.update({
      Birthplace: value,
      ...additional
    });
  };

  updateMethods = (values: CheckboxModel) => {
    const method = values.value;
    const selected = [...((this.props.Methods || {}).values || [])];

    if (selected.includes(method)) {
      // Remove the relation if it was previously selected
      selected.splice(selected.indexOf(method), 1);
    } else {
      // Add the relation if it wasn't already
      selected.push(method);
    }

    if (selected.includes('Other')) {
      this.update({
        Methods: { values: selected },
      });
    } else {
      this.update({
        Methods: { values: selected },
        MethodsComments: {},
      });
    }
  };

  updateAddressField = (values: RelationshipsRelativeItemModel['Address']) => {
    if (countryString(values.country) === 'United States') {
      this.update({
        AlternateAddress: {},
        Address: values,
      });
    } else {
      this.update({
        Address: values,
      });
    }
  };

  updateFrequency = (values: RelationshipsRelativeItemModel['Frequency']) => {
    if ((values || {}).value !== 'Other') {
      this.update({
        Frequency: values,
        FrequencyComments: {},
        FrequencyExplanation: {},
      });
    } else {
      this.update({
        Frequency: values,
      });
    }
  };

  updateFrequencyExplanation = (value: RelationshipsRelativeItemModel['FrequencyExplanation']) => {
    this.update({
      FrequencyExplanation: value,
    });
  };

  updateDocumentNumber = (values: RelationshipsRelativeItemModel['DocumentNumber']) => {
    this.update({
      DocumentNumber: values,
      DocumentNumberNotApplicable: values?.value
        ? { applicable: true }
        : this.props.DocumentNumberNotApplicable,
      DocumentNumberExplanation: values?.value ? {} : this.props.DocumentNumberExplanation, // when there is a number there is not a second pass explanation
      ResidenceDocumentNumber: values?.value ? {} : this.props.ResidenceDocumentNumber, // only a Residence Number or U.S. citizen other Number may exist simultaniously.
    });
  };

  updateDocumentNumberNotApplicable = (
    values: RelationshipsRelativeItemModel['DocumentNumberNotApplicable']
  ) => {
    this.update({
      DocumentNumberNotApplicable: values,
      DocumentNumber: values?.applicable ? this.props.DocumentNumber : {},
      ResidenceDocumentNumber: values?.applicable ? this.props.ResidenceDocumentNumber : {},
      NoDocumentNumber: values?.applicable ? this.props.NoDocumentNumber : {},
    });
  };

  updateDocumentNumberExplanation = (
    values: RelationshipsRelativeItemModel['DocumentNumberExplanation']
  ) => {
    this.update({ DocumentNumberExplanation: values });
  };

  updateResidenceDocumentNumber = (
    values: RelationshipsRelativeItemModel['ResidenceDocumentNumber']
  ) => {
    this.update({
      ResidenceDocumentNumber: values,
      DocumentNumberExplanation: values?.value ? {} : this.props.DocumentNumberExplanation, // when there is a number there is not a second pass explanation
      DocumentNumber: values?.value ? {} : this.props.DocumentNumber, // only a Residence Number or U.S. citizen other Number may exist simultaniously.
      DocumentNumberNotApplicable: values?.value
        ? { applicable: true }
        : this.props.DocumentNumberNotApplicable,
    });
  };

  updateEmployer = (values: RelationshipsRelativeItemModel['Employer']) => {
    this.update({
      Employer: values,
      EmployerNotApplicable: { applicable: true },
      NeverWorked: { ...this.props.NeverWorked, checked: false }
    });
  };

  updateEmployerNotApplicable = (
    values: RelationshipsRelativeItemModel['EmployerNotApplicable']
  ) => {
    this.update({
      EmployerNotApplicable: values,
      EmployerAddress: {},
      RelativeOccupation: {},
      Employer: {},
      NeverWorked: values?.applicable ? this.props.NeverWorked : {},
    });
  };

  updateNeverWorked = (value: RelationshipsRelativeItemModel['NeverWorked']) => {
    this.update({
      Employer: value?.checked ? {} : this.props.Employer,
      NeverWorked: value
    });
  };

  updateNoDocumentNumber = (value: RelationshipsRelativeItemModel['NoDocumentNumber']) => {
    const hasNoDocumentNumber = value?.checked === true;

    this.update({
      ResidenceDocumentNumber: hasNoDocumentNumber ? {} : this.props.ResidenceDocumentNumber,
      NoDocumentNumber: value
    });
  };

  updateAlternateAddressNotApplicable = (
    values: RelationshipsRelativeItemModel['AlternateAddressNotApplicable']
  ) => {
    this.update({
      AlternateAddressNotApplicable: values,
      AlternateAddress: {},
    });
  };

  updateAlternateAddressExplanation = (
    values: RelationshipsRelativeItemModel['AlternateAddressExplanation']
  ) => {
    this.updateField('AlternateAddressExplanation', values);
  };

  updateEmployerAddress = (values: RelationshipsRelativeItemModel['EmployerAddress']) => {
    this.update({
      EmployerAddress: values,
      EmployerAddressNotApplicable: { applicable: true },
    });
  };

  updateEmployerAddressNotApplicable = (
    values: RelationshipsRelativeItemModel['EmployerAddressNotApplicable']
  ) => {
    this.update({
      EmployerAddressNotApplicable: values,
      EmployerAddress: {},
    });
  };

  updateEmployerRelationship = (values: RelationshipsRelativeItemModel['EmployerRelationship']) => {
    this.update({
      EmployerRelationship: values,
      EmployerRelationshipNotApplicable: { applicable: true },
    });
  };

  updateEmployerRelationshipNotApplicable = (
    values: RelationshipsRelativeItemModel['EmployerRelationshipNotApplicable']
  ) => {
    this.update({
      EmployerRelationshipNotApplicable: values,
      EmployerRelationship: values?.applicable ? this.props.EmployerRelationship : {},
    });
  };

  updatePvqRelation = (value: RelationshipsRelativeItemModel['PvqRelation']) => {
    this.update({
      PvqRelation: value,
    });
  };

  updatePvqEmployerRelationship = (values: RelationshipsRelativeItemModel['PvqEmployerRelationship']) => {
    this.update({
      PvqEmployerRelationship: values,
      PvqOtherGovtAgencyExplanation: {},
    });
  };

  updatePvqOtherGovtAgencyExplanation = (values: RelationshipsRelativeItemModel['PvqOtherGovtAgencyExplanation']) => {
    this.update({
      PvqOtherGovtAgencyExplanation: values,
    });
  };

  updateParentRelation = (values: RelationshipsRelativeItemModel['Relation']) => {
    // Reset all DNK values when new relative type is selected.
    // Clear any fields that contain prefills
    if (
      this.props.Name
      && this.props.Name.first === i18n.t('relationships.relatives.prefill.name.first')
      && this.props.Name.last === i18n.t('relationships.relatives.prefill.name.last')
    ) {
      this.update({
        Relation: values,
        ParentNotApplicable: { applicable: true },
        EntryExplanation: {},
        Name: {},
        Birthdate: {},
        Birthplace: {},
        Citizenship: {},
        IsDeceased: {},
        Aliases: {},
        MaidenSameAsListed: {},
      });
    } else {
      this.update({
        Relation: values,
        ParentNotApplicable: { applicable: true },
        EntryExplanation: {},
      });
    }
  };

  updateParentNotApplicableAndPrefills = (
    values: RelationshipsRelativeItemModel['ParentNotApplicable'],
    relation: RelationshipsRelativeItemModel['Relation']['value']
  ) => {
    this.update({
      // clicking /unclicking DNK check values should alwasy clear the entries
      ParentNotApplicable: values,
      Name: {},
      Birthdate: {},
      Birthplace: {},
      Citizenship: {},
      IsDeceased: {},
      Aliases: {},
      MaidenSameAsListed: {},
      EntryExplanation: {},
    });
  };

  updateParentCitizenshipNotApplicable = (values: RelationshipsRelativeItemModel['ParentCitizenshipStatusNotApplicable']) => {
    this.update({
      ParentCitizenshipStatusNotApplicable: values,
      CitizenshipStatus: values.applicable ? this.props.CitizenshipStatus : {},
    });
  };

  updateMothersMaidenNameComments = (values: RelationshipsRelativeItemModel['MothersMaidenNameComments']) => {
    this.update({
      MothersMaidenNameComments: values
    });
  };

  updateAliasesComments = (values: RelationshipsRelativeItemModel['AliasesComments']) => {
    this.update({
      AliasesComments: values
    });
  };

  updateAliasesSummaryComments = (values: RelationshipsRelativeItemModel['AliasesSummaryComments']) => {
    this.update({
      AliasesSummaryComments: values
    });
  };

  updateDeceasedComments = (values: RelationshipsRelativeItemModel['DeceasedComments']) => {
    this.update({
      DeceasedComments: values
    });
  };

  updateForeignRelativeComments = (values: RelationshipsRelativeItemModel['ForeignRelativeComments']) => {
    this.update({
      ForeignRelativeComments: values
    });
  };

  updateOptionalComments = (values: RelationshipsRelativeItemModel['OptionalComments']) => {
    this.update({
      OptionalComments: values
    });
  };

  updateEmployerComments = (values: RelationshipsRelativeItemModel['EmployerComments']) => {
    this.update({
      EmployerComments: values
    });
  };

  updateHasAffiliationComments = (values: RelationshipsRelativeItemModel['HasAffiliationComments']) => {
    this.update({
      HasAffiliationComments: values
    });
  };

  updateForeignAffiliationComments = (values: RelationshipsRelativeItemModel['ForeignAffiliationComments']) => {
    this.update({
      ForeignAffiliationComments: values
    });
  };

  updateAddressComments = (values: RelationshipsRelativeItemModel['AddressComments']) => {
    this.update({
      AddressComments: values,
    });
  };

  updateBirthdateNotApplicable = (values: RelationshipsRelativeItemModel['BirthdateNotApplicable']) => {
    this.update({
      BirthdateNotApplicable: values,
      Birthdate: values?.applicable ? this.props.Birthdate : {},
    });
  };

  updateBirthplaceNotApplicable = (values: RelationshipsRelativeItemModel['BirthplaceNotApplicable']) => {
    this.update({
      BirthplaceNotApplicable: values,
      Birthplace: values?.applicable ? this.props.Birthplace : {},
    });
  };

  updateRelativeRelationshipNotApplicable = (values: RelationshipsRelativeItemModel['RelativeRelationshipNotApplicable']) => {
    this.update({
      RelativeRelationshipNotApplicable: values,
      PvqEmployerRelationship: values?.applicable ? this.props.PvqEmployerRelationship : {},
    });
  };

  updateHasAffiliation = (value: RelationshipsRelativeItemModel['HasAffiliation']) => {
    this.update({
      HasAffiliation: value,
    });
  };

  updateAffiliationNotApplicable = (values: RelationshipsRelativeItemModel['AffiliationNotApplicable']) => {
    this.update({
      AffiliationNotApplicable: values,
      HasAffiliation: values?.applicable ? this.props.HasAffiliation : {},
      PvqEmployerRelationship: values?.applicable ? this.props.PvqEmployerRelationship : {}
    });
  };

  updateDeceasedNotApplicable = (values: RelationshipsRelativeItemModel['DeceasedNotApplicable']) => {
    this.update({
      DeceasedNotApplicable: values,
    });
  };

  updateAddressNotApplicable = (value) => {
    this.update({
      AddressNotApplicable: value,
      Address: value.applicable ? this.props.Address : {},
      AddressExplanation: value.applicable ? {} : this.props.AddressExplanation,
    });
  };

  updateCourtAddressNotApplicable = (value) => {
    this.update({
      CourtAddressNotApplicable: value,
      CourtAddress: value.applicable ? this.props.CourtAddress : {},
      CourtAddressExplanation: value.applicable ? {} : this.props.CourtAddressExplanation,
    });
  };

  updateFirstContactNotApplicable = (value) => {
    this.update({
      FirstContactNotApplicable: value,
      FirstContact: value.applicable ? this.props.FirstContact : {},
      FirstContactExplanation: value.applicable ? {} : this.props.FirstContactExplanation,
    });
  };

  updateCitizenshipCountryNotApplicable = (value) => {
    this.update({
      Citizenship: value.applicable ? this.props.Citizenship : {},
      CitizenshipCountryNotApplicable: value,
    });
  };

  updateHasRelativeAffiliations = (value: RelationshipsRelativeItemModel['HasRelativeAffiliations']) => {
    this.update({
      HasRelativeAffiliations: value,
    });
  };

  updateRelativeOccupation = (value: RelationshipsRelativeItemModel['RelativeOccupation']) => {
    this.update({
      RelativeOccupation: value
    });
  };

  updateInContact = (value: RelationshipsRelativeItemModel['InContact']) => {
    this.update({
      InContact: value
    });
  };

  branchComment = () => {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <Comments
        title={i18n.t('relationships.comments.optional')}
        name={`${attribute}AliasesComments`}
        className="optional-comment"
        attribute={`${attribute}AliasesComments`}
        {...this.props.AliasesComments}
        onUpdate={this.updateAliasesComments}
        required={false}
      />
    );
  };

  hasExpirationDate = (): boolean => {
    if (!this.props.CitizenshipStatus || !this.props.CitizenshipDocumentation) {
      return false;
    }
    const status = this.props.CitizenshipStatus?.value;
    const documentation = this.props.CitizenshipDocumentation?.value;

    if (status !== PVQ_CITIZEN) {
      if ((status === PVQ_FOREIGN_BORN && documentation === DOC_Other)
        || (status === PVQ_NATURALIZED && documentation === DOC_Other)
        || (status === PVQ_DERIVED && documentation === DOC_Other)
        || isNationalOrNonCitizen(status)) {
        return true;
      }
    }
    return false;
  };

  pvqNotDeceasedFormQuestions = (): ReactElement => {
    const attribute = `List.items[${this.props.$index}].Item`;
    const mother = (this.props.Relation || {}).value === 'Mother';
    return (
      (
        <>
          <BranchCollection
            {...this.props.Aliases}
            attribute={`${attribute}.Aliases`}
            branchName="has_alias"
            label={i18n.t('relationships.pvq.relatives.heading.alias.branch')}
            appendLabel={i18n.t('relationships.pvq.relatives.heading.alias.additional')}
            className="relative-alias"
            onUpdate={(value: RelationshipsRelativeItemModel['Aliases']) => {
              this.updateField('Aliases', value);
            }}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <div>
              <Alias
                name="Item"
                attribute={`${attribute}.Aliases`}
                minDate={this.props.Birthdate}
                relationship="Other"
                onError={this.props.onError}
                formType={this.props.formType}
                hideMaiden={mother && this.props.MaidenSameAsListed?.value === 'Yes'}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                bind
              />
            </div>
          </BranchCollection>
          <Field
            attribute={`${attribute}.Address`}
            title={i18n.t('relationships.pvq.relatives.heading.deceased.address')}
            optional
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="address"
            exactAttribute
            hideTitle
          >
            <PVQLocationUnknown
              attribute={`${attribute}.Address`}
              name="Address"
              className="relative-address"
              {...this.props.Address}
              inUsaUnknown={this.props.InUsaNotApplicable}
              inUsaUnknownLabel={i18n.t('reference.label.idk')}
              label={i18n.t('relationships.pvq.relatives.heading.deceased.address')}
              domesticTitle={i18n.t('relationships.pvq.relatives.heading.deceased.domestic')}
              foreignTitle={i18n.t('relationships.pvq.relatives.heading.deceased.foreign')}
              addressBooks={this.props.addressBooks}
              addressBook={this.props.addressBook}
              dispatch={this.props.dispatch}
              layout={Location.ADDRESS}
              geocode
              onUpdate={(value: RelationshipsRelativeItemModel['Address']) => {
                this.updateAddressField(value);
              }}
              onUpdateInUsaUnknown={this.updateInUsaNotApplicable}
              onError={this.props.onError}
              required={this.props.required}
              showInternationalStreet
              showInUsaUnknown
              showIsInstallationUnknownIfInternational
              showIsInstallationUnknownIfDomestic
              showMilitaryInstallation
              hideInstallationNameUnknownWhenForeign
              showApoFpoUnknown
            />
          </Field>

          <Field
            title={i18n.t('relationships.pvq.relatives.heading.deceased.citizenshipStatus')}
            titleSize="h4"
            attribute={`${attribute}.CitizenshipStatus`}
            className="relative-citizenship-documentation no-margin-bottom"
          >
            <NotApplicable
              name="ParentCitizenshipStatusNotApplicable"
              {...this.props.ParentCitizenshipStatusNotApplicable}
              label={i18n.t('relationships.relatives.label.idk')}
              or={i18n.t('relationships.relatives.para.or')}
              onUpdate={this.updateParentCitizenshipNotApplicable}
              attribute="ParentCitizenshipStatusNotApplicable"
            >
              <RadioGroup
                className="relative-citizenship-status option-list option-list-vertical"
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.CitizenshipStatus`}
                selectedValue={(this.props.CitizenshipStatus || {}).value}
              >
                {this.getPvqCitizenshipStatus().map((document) => (
                  <Radio
                    key={document.name}
                    name={document.name}
                    label={document.label}
                    value={document.value}
                    className={document.name}
                    onError={this.props.onError}
                    onUpdate={this.updateCitizenshipStatus}
                  />
                ))}
              </RadioGroup>
            </NotApplicable>

            {this.props.CitizenshipStatus?.value === PVQ_FOREIGN_BORN && this.pvqForeignBornFormQuestions()}

            {this.props.CitizenshipStatus?.value === PVQ_NATURALIZED && this.pvqNaturalizedFormQuestions()}

            {this.props.CitizenshipStatus?.value === PVQ_DERIVED && this.pvqDerivedFormQuestions()}

            {isNotUSCLivesInUS(this.props.CitizenshipStatus?.value, getCountryValue(this.props.Address?.country)) && this.pvqNotCitizenFormQuestions()}

            {(this.props.CitizenshipStatus?.value !== PVQ_CITIZEN && this.props.CitizenshipDocumentation?.value === DOC_Other) && (
              <div>
                <Field
                  title={i18n.t('relationships.pvq.relatives.heading.us.otherTitle')}
                  titleSize="h4"
                  attribute={`${attribute}.OtherCitizenshipDocumentation`}
                >
                  <Textarea
                    name="OtherFormTitle"
                    attribute={`${attribute}.OtherCitizenshipDocumentation`}
                    className="other-form-title"
                    {...this.props.OtherCitizenshipDocumentation}
                    onError={this.props.onError}
                    onUpdate={this.updateOtherCitizenshipDocumentation}
                    required={this.props.required}
                  />
                </Field>
                <Field
                  title={i18n.t('relationships.pvq.relatives.heading.us.number')}
                  adjustFor="buttons"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ResidenceDocumentNumber`}
                  exactAttribute
                  shrink
                >
                  <NotApplicable
                    {...this.props.DocumentNumberNotApplicable}
                    name="DocumentNumberNotApplicable"
                    attribute={`${attribute}.DocumentNumberNotApplicable`}
                    label={i18n.t('relationships.pvq.relatives.label.idk')}
                    or={i18n.t('relationships.relatives.para.or')}
                    onError={this.props.onError}
                    onUpdate={this.updateDocumentNumberNotApplicable}
                  >
                    <Text
                      attribute={`${attribute}.ResidenceDocumentNumber`}
                      name="OtherFormNumber"
                      className="other-form-number"
                      maxlength="30"
                      pattern={alphaNumericRegEx}
                      prefix="alphanumeric"
                      {...this.props.ResidenceDocumentNumber}
                      onError={this.props.onError}
                      onUpdate={this.updateResidenceDocumentNumber}
                      required={this.props.required}
                    />

                    <Checkbox
                      label={i18n.t('relationships.pvq.relatives.label.nonum')}
                      value="Yes"
                      checked={this.props.NoDocumentNumber?.checked}
                      className="No-document-number"
                      onUpdate={this.updateNoDocumentNumber}
                      onError={this.props.onError}
                    />
                  </NotApplicable>
                </Field>
              </div>
            )}
          </Field>
          <div>
            <Branch
              name="in_contact"
              label={i18n.t('relationships.pvq.relatives.heading.inContact')}
              labelSize="h4"
              attribute={`${attribute}.InContact`}
              className="eapp-field-wrap relative-maiden-diff"
              {...this.props.InContact}
              onUpdate={this.updateInContact}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              onError={this.props.onError}
            />
            {this.props.InContact?.value === 'Yes' && this.pvqInContactFormQuestions()}
            <Field
              title={i18n.t('relationships.pvq.relatives.heading.employer.name')}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Employer`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.EmployerNotApplicable}
                name="EmployerNotApplicable"
                attribute={`${attribute}.EmployerNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateEmployerNotApplicable}
              >
                <Text
                  attribute={`${attribute}.Employer`}
                  name="Employer"
                  className="relative-employer"
                  {...this.props.Employer}
                  onError={this.props.onError}
                  onUpdate={this.updateEmployer}
                  required={this.props.required}
                />
                <Checkbox
                  label={i18n.t('relationships.pvq.relatives.label.neverWorked')}
                  value="Yes"
                  checked={this.props.NeverWorked?.checked}
                  className="NeverWorked"
                  onUpdate={this.updateNeverWorked}
                  onError={this.props.onError}
                />
              </NotApplicable>
            </Field>
            {this.props.Employer?.value !== '' && this.props.EmployerNotApplicable?.applicable !== false && !this.props.NeverWorked?.checked && this.pvqRelativeEmployedQuestions()}

            <BranchCollection
              {...this.props.HasRelativeAffiliations}
              attribute={`${attribute}.HasRelativeAffiliations`}
              branchName="has_affiliations"
              label={i18n.t('relationships.pvq.relatives.heading.employer.affiliated')}
              labelSize="h4"
              appendLabel={i18n.t('relationships.pvq.relatives.heading.employer.anotherAffiliated')}
              className="relative-affiliation"
              showDNK
              dnkLabel={i18n.t('relationships.pvq.relatives.heading.employer.idkLabel')}
              onUpdate={this.updateHasRelativeAffiliations}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              onError={this.props.onError}
            >
              <RelativeAffiliation
                name="Item"
                attribute={`${attribute}.HasRelativeAffiliations`}
                onError={this.props.onError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                bind
              />
            </BranchCollection>
          </div>
        </>
      )
    );
  };

  pvqInContactFormQuestions = (): ReactElement => {
    const attribute = `List.items[${this.props.$index}].Item`;
    const relativeContactMinDate = pickDate([this.props.applicantBirthdate, this.props.Birthdate]);

    return (
      <div>
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.address.firstcontact')}
          attribute={`${attribute}.FirstContact`}
          help="relationships.relatives.help.firstcontact"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          exactAttribute
        >
          <DateControl
            attribute={`${attribute}.FirstContact`}
            name="FirstContact"
            className="relative-first-contact"
            {...this.props.FirstContact}
            minDate={relativeContactMinDate}
            minDateEqualTo
            onError={this.props.onError}
            onUpdate={(value: RelationshipsRelativeItemModel['FirstContact']) => {
              this.updateField('FirstContact', value);
            }}
            required={this.props.required}
            hideDay
          />
        </Field>
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.address.lastcontact')}
          attribute={`${attribute}.LastContact`}
          help="relationships.relatives.help.lastcontact"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          shrink
        >
          <DateControl
            attribute={`${attribute}.LastContact`}
            name="LastContact"
            className="relative-last-contact"
            {...this.props.LastContact}
            prefix="relative"
            minDate={this.props.FirstContact}
            minDateEqualTo
            onError={this.props.onError}
            onUpdate={(value: RelationshipsRelativeItemModel['LastContact']) => {
              this.updateField('LastContact', value);
            }}
            required={this.props.required}
            hideDay
          />
        </Field>
        <Field
          attribute={`${attribute}.Methods`}
          title={i18n.t('relationships.pvq.relatives.heading.address.methods')}
          onError={this.props.onError}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
        >
          <div>
            {i18n.m('relationships.relatives.para.checkall')}
            <CheckboxGroup
              attribute={`${attribute}.Methods`}
              className="relative-methods option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValues={(this.props.Methods || {}).values}
            >
              <Checkbox
                name="methods-inperson"
                label={i18n.t('relationships.pvq.relatives.label.methods.inperson')}
                value="In-person"
                className="methods-inperson"
                onError={this.props.onError}
                onUpdate={this.updateMethods}
              />
              <Checkbox
                name="methods-telephone"
                label={i18n.t('relationships.pvq.relatives.label.methods.telephone')}
                value="Telephone"
                className="methods-telephone"
                onError={this.props.onError}
                onUpdate={this.updateMethods}
              />
              <Checkbox
                name="methods-electronic"
                label={i18n.t('relationships.pvq.relatives.label.methods.electronic')}
                value="Electronic means (for example, email, texting, chat rooms)"
                className="methods-electronic"
                onError={this.props.onError}
                onUpdate={this.updateMethods}
              />
              <Checkbox
                name="methods-written"
                label={i18n.t('relationships.pvq.relatives.label.methods.written')}
                value="Written correspondence"
                className="methods-written"
                onError={this.props.onError}
                onUpdate={this.updateMethods}
              />
              <Checkbox
                name="methods-other"
                label={i18n.t('relationships.pvq.relatives.label.methods.other')}
                value="Other"
                className="methods-other"
                onError={this.props.onError}
                onUpdate={this.updateMethods}
              />
              <Checkbox
                name="methods-none"
                label={i18n.t('relationships.pvq.relatives.label.methods.none')}
                value="None"
                className="methods-none"
                onError={this.props.onError}
                onUpdate={this.updateMethods}
              />
            </CheckboxGroup>
          </div>
        </Field>
        {this.props.Methods?.values?.includes('Other') && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.heading.pleaseExplain')}
              attribute={`${attribute}.MethodsComments`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                attribute={`${attribute}.MethodsComments`}
                name="MethodsComments"
                className="document-number-dnk-comments"
                {...this.props.MethodsComments}
                onUpdate={(value: RelationshipsRelativeItemModel['MethodsComments']) => {
                  this.updateField('MethodsComments', value);
                }}
                required={this.props.required}
                onError={this.props.onError}
              />
            </Field>
          </div>
        )}
        <Field
          attribute={`${attribute}.Frequency`}
          title={i18n.t('relationships.pvq.relatives.heading.address.frequency')}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="big-buttons"
        >
          <RadioGroup
            className="relative-frequency option-list option-list-vertical"
            attribute={`${attribute}.Frequency`}
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.Frequency?.value}
          >
            {this.getFrequency().map((freq) => (
              <Radio
                key={freq.name}
                name={freq.name}
                label={freq.label}
                value={freq.value}
                className={freq.name}
                onError={this.props.onError}
                onUpdate={this.updateFrequency}
              />
            ))}
          </RadioGroup>
          {this.props.Frequency?.value === 'Other' && (
            <div>
              <Field
                title={i18n.t('relationships.pvq.relatives.heading.pleaseExplain')}
                attribute={`${attribute}.FrequencyExplanation`}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  attribute={`${attribute}.FrequencyExplanation`}
                  name="FrequencyExplanation"
                  {...this.props.FrequencyExplanation}
                  onUpdate={this.updateFrequencyExplanation}
                  required={this.props.required}
                  onError={this.props.onError}
                />
              </Field>
            </div>
          )}
        </Field>
      </div>
    );
  };

  pvqForeignBornFormQuestions = (): ReactElement => {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      (
        <div>
          <Field
            title={i18n.t('relationships.pvq.relatives.heading.us.foreignDocument')}
            titleSize="h4"
            attribute={`${attribute}.CitizenshipDocumentation`}
            className="relative-citizenship-documentation no-margin-bottom"
          >
            <RadioGroup
              className="relative-abroad option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              attribute={`${attribute}.CitizenshipDocumentation`}
              selectedValue={(this.props.CitizenshipDocumentation || {}).value}
            >
              {this.getPvqUsForeignDocumentation().map((document) => (
                <Radio
                  key={document.name}
                  name={document.name}
                  label={document.label}
                  value={document.value}
                  className={document.name}
                  onError={this.props.onError}
                  onUpdate={this.updateCitizenshipDocumentation}
                />
              ))}
            </RadioGroup>
          </Field>
          {(this.hasExpirationDate() && this.props.ParentCitizenshipStatusNotApplicable?.applicable !== false) && this.pvqDocumentExpirationQuestions()}
          {this.props.CitizenshipDocumentation?.value !== DOC_Other && (
            <div>
              <Field
                title={i18n.t('relationships.pvq.relatives.heading.us.serial')}
                adjustFor="buttons"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.DocumentNumber`}
                exactAttribute
                shrink
              >
                <NotApplicable
                  {...this.props.DocumentNumberNotApplicable}
                  name="DocumentNumberNotApplicable"
                  attribute={`${attribute}.DocumentNumberNotApplicable`}
                  label={i18n.t('relationships.pvq.relatives.label.idkNoNumber')}
                  or={i18n.t('relationships.relatives.para.or')}
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumberNotApplicable}
                >
                  <Text
                    attribute={`${attribute}.DocumentNumber`}
                    name="DocumentNumber"
                    className="usforeign-serial-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    {...this.props.DocumentNumber}
                    onError={this.props.onError}
                    onUpdate={this.updateDocumentNumber}
                    required={this.props.required}
                  />
                </NotApplicable>
              </Field>
            </div>
          )}
        </div>
      )
    );
  };

  pvqNaturalizedFormQuestions = (): ReactElement => {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div>
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.us.naturalizedDocument')}
          titleSize="h4"
          attribute={`${attribute}.CitizenshipDocumentation`}
          className="relative-citizenship-documentation no-margin-bottom"
        >
          <RadioGroup
            className="relative-naturalized option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.CitizenshipDocumentation`}
            selectedValue={(this.props.CitizenshipDocumentation || {}).value}
          >
            {this.getPvqNaturalizedDocumentation().map((document) => (
              <Radio
                key={document.name}
                name={document.name}
                label={document.label}
                value={document.value}
                className={document.name}
                onError={this.props.onError}
                onUpdate={this.updateCitizenshipDocumentation}
              />
            ))}
          </RadioGroup>
        </Field>
        {(this.hasExpirationDate() && this.props.ParentCitizenshipStatusNotApplicable?.applicable !== false) && this.pvqDocumentExpirationQuestions()}
        {this.props.CitizenshipDocumentation?.value === DOC_NaturalizedCertificate && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.naturalized.alienNumber')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DocumentNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.DocumentNumberNotApplicable}
                name="DocumentNumberNotApplicable"
                attribute={`${attribute}.DocumentNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateDocumentNumberNotApplicable}
              >
                <Text
                  attribute={`${attribute}.DocumentNumber`}
                  name="DocumentNumber"
                  className="naturalized-certificate-alien"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.DocumentNumber}
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.naturalized.certNumber')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CertificateNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.CertificateNumberNotApplicable}
                name="CertificateNumberNotApplicable"
                attribute={`${attribute}.CertificateNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateCertificateNumberNotApplicable}
              >
                <Text
                  attribute={`${attribute}.CertificateNumber`}
                  name="CertificateNumber"
                  className="naturalized-certificate-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.CertificateNumber}
                  onError={this.props.onError}
                  onUpdate={this.updateCertificateNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.naturalized.courtName')}
              attribute={`${attribute}.CourtName`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <NotApplicable
                {...this.props.CourtNameNotApplicable}
                name="CourtNameNotApplicable"
                attribute={`${attribute}.CourtNameNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateCourtNameNotApplicable}
              >
                <Text
                  attribute={`${attribute}.CourtName`}
                  name="CourtName"
                  className="naturalized-courtname"
                  {...this.props.CourtName}
                  onError={this.props.onError}
                  onUpdate={this.updateCourtName}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
            <Field
              attribute={`${attribute}.CourtAddress`}
              optional
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="labels"
              exactAttribute
            >
              <NotApplicable
                name="CourtAddressNotApplicable"
                {...this.props.CourtAddressNotApplicable}
                label={i18n.t('relationships.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onUpdate={this.updateCourtAddressNotApplicable}
                attribute="CourtAddressNotApplicable"
                includeNAConfirmation={Boolean(this.props.CourtAddress
                  && (this.props.CourtAddress.city
                    || this.props.CourtAddress.state
                  ))}
              >
                <PVQLocation
                  attribute={`${attribute}.CourtAddress`}
                  name="CourtAddress"
                  {...this.props.CourtAddress}
                  domesticTitle={i18n.t('relationships.pvq.relatives.label.naturalized.courtAddress')}
                  foreignTitle={i18n.t('relationships.pvq.relatives.label.naturalized.courtAddress')}
                  layout={Layout.US_ADDRESS}
                  country="United States"
                  geocode
                  className="relative-courtaddress"
                  onError={this.props.onError}
                  onUpdate={this.updateCourtAddress}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
          </div>
        )}
        {(this.props.CitizenshipDocumentation?.value === DOC_NaturalizedPermanentAlien
          || this.props.CitizenshipDocumentation?.value === DOC_NaturalizedMriv)
          && (
            <div>
              <Field
                title={i18n.t('relationships.pvq.relatives.label.naturalized.alienNumberMriv')
                  .split('\n')
                  .map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                adjustFor="buttons"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.DocumentNumber`}
                exactAttribute
                shrink
              >
                <NotApplicable
                  {...this.props.DocumentNumberNotApplicable}
                  name="DocumentNumberNotApplicable"
                  attribute={`${attribute}.DocumentNumberNotApplicable`}
                  label={i18n.t('relationships.pvq.relatives.label.idk')}
                  or={i18n.t('relationships.relatives.para.or')}
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumberNotApplicable}
                >
                  <Text
                    attribute={`${attribute}.DocumentNumber`}
                    name="DocumentNumber"
                    className="naturalized-perm-mriv-alien-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    {...this.props.DocumentNumber}
                    onError={this.props.onError}
                    onUpdate={this.updateDocumentNumber}
                    required={this.props.required}
                  />
                </NotApplicable>
              </Field>
            </div>
          )}
      </div>
    );
  };

  pvqDerivedFormQuestions = (): ReactElement => {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div>
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.us.derivedDocument')}
          titleSize="h4"
          attribute={`${attribute}.CitizenshipDocumentation`}
          className="relative-citizenship-documentation no-margin-bottom"
        >
          <RadioGroup
            className="relative-derived option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.CitizenshipDocumentation`}
            selectedValue={(this.props.CitizenshipDocumentation || {}).value}
          >
            {this.getPvqDerivedDocumentation().map((document) => (
              <Radio
                key={document.name}
                name={document.name}
                label={document.label}
                value={document.value}
                className={document.name}
                onError={this.props.onError}
                onUpdate={this.updateCitizenshipDocumentation}
              />
            ))}
          </RadioGroup>
        </Field>
        {(this.hasExpirationDate() && this.props.ParentCitizenshipStatusNotApplicable?.applicable !== false) && this.pvqDocumentExpirationQuestions()}

        {this.props.CitizenshipDocumentation?.value === DOC_DerivedCertificate && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.derived.alienNumber')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DocumentNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.DocumentNumberNotApplicable}
                name="DocumentNumberNotApplicable"
                attribute={`${attribute}.DocumentNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateDocumentNumberNotApplicable}
              >
                <Text
                  attribute={`${attribute}.DocumentNumber`}
                  name="DocumentNumber"
                  className="derived-alien-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.DocumentNumber}
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.derived.certNumber')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CertificateNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.CertificateNumberNotApplicable}
                name="CertificateNumberNotApplicable"
                attribute={`${attribute}.CertificateNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateCertificateNumberNotApplicable}
              >
                <Text
                  attribute={`${attribute}.CertificateNumber`}
                  name="CertificateNumber"
                  className="derived-certificate-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.CertificateNumber}
                  onError={this.props.onError}
                  onUpdate={this.updateCertificateNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
          </div>
        )}
        {(this.props.CitizenshipDocumentation?.value === DOC_DerivedPermanentAlien || this.props.CitizenshipDocumentation?.value === DOC_DerivedMriv) && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.derived.alienNumberMriv')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DocumentNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.DocumentNumberNotApplicable}
                name="DocumentNumberNotApplicable"
                attribute={`${attribute}.DocumentNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateDocumentNumberNotApplicable}
              >
                <Text
                  attribute={`${attribute}.DocumentNumber`}
                  name="DocumentNumber"
                  className="derived-permanent-mriv-alien"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.DocumentNumber}
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
          </div>
        )}
      </div>
    );
  };

  pvqNotCitizenFormQuestions = (): ReactElement => {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div>
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.us.residence')}
          titleSize="h4"
          attribute={`${attribute}.Residency`}
          className="relative-citizenship-documentation no-margin-bottom"
        >
          <RadioGroup
            className="relative-document option-list option-list-vertical"
            attribute={`${attribute}.Residency`}
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.Residency || {}).value}
          >
            {this.getPvqResidency().map((document) => (
              <Radio
                key={document.name}
                name={document.name}
                label={document.label}
                value={document.value}
                className={document.name}
                onError={this.props.onError}
                onUpdate={this.updateResidency}
              />
            ))}
          </RadioGroup>
        </Field>
        {this.props.Residency?.value === 'Other' && (
          <Field
            title={i18n.t('relationships.pvq.relatives.heading.other')}
            titleSize="h4"
            attribute={`${attribute}.AddressExplanation`}
          >
            <Textarea
              attribute={`${attribute}.AddressExplanation`}
              name="ResidencyExplanation"
              className="relative-residency-other-explanation"
              {...this.props.AddressExplanation}
              onError={this.props.onError}
              onUpdate={this.updateAddressExplanation}
              required={this.props.required}
            />
          </Field>
        )}
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.us.notUsDocument')}
          titleSize="h4"
          attribute={`${attribute}.CitizenshipDocumentation`}
          className="relative-citizenship-documentation no-margin-bottom"
        >
          <RadioGroup
            className="relative-document option-list option-list-vertical"
            attribute={`${attribute}.CitizenshipDocumentation`}
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.CitizenshipDocumentation || {}).value}
          >
            {this.getPvqNonCitizenDocumentation().map((document) => (
              <Radio
                key={document.name}
                name={document.name}
                label={document.label}
                value={document.value}
                className={document.name}
                onError={this.props.onError}
                onUpdate={this.updateCitizenshipDocumentation}
              />
            ))}
          </RadioGroup>
        </Field>
        {(this.hasExpirationDate() && this.props.ParentCitizenshipStatusNotApplicable?.applicable !== false) && this.pvqDocumentExpirationQuestions()}
        {(this.props.CitizenshipDocumentation?.value === DOC_NonPermanent
          || this.props.CitizenshipDocumentation?.value === DOC_NonEmployment
          || this.props.CitizenshipDocumentation?.value === DOC_NonMRIV)
          && (
            <div>
              <Field
                title={i18n.t('relationships.pvq.relatives.label.document.alienNumber')
                  .split('\n')
                  .map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                adjustFor="buttons"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.DocumentNumber`}
                exactAttribute
                shrink
              >
                <NotApplicable
                  {...this.props.DocumentNumberNotApplicable}
                  name="DocumentNumberNotApplicable"
                  attribute={`${attribute}.DocumentNumberNotApplicable`}
                  label={i18n.t('relationships.pvq.relatives.label.idk')}
                  or={i18n.t('relationships.relatives.para.or')}
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumberNotApplicable}
                >
                  <Text
                    attribute={`${attribute}.DocumentNumber`}
                    name="DocumentNumber"
                    className="non-us-alien-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    {...this.props.DocumentNumber}
                    onError={this.props.onError}
                    onUpdate={this.updateDocumentNumber}
                    required={this.props.required}
                  />
                </NotApplicable>
              </Field>
            </div>
          )}
        {this.props.CitizenshipDocumentation?.value === DOC_NonArrival && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.document.i94Number')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DocumentNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.DocumentNumberNotApplicable}
                name="DocumentNumberNotApplicable"
                attribute={`${attribute}.DocumentNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateDocumentNumberNotApplicable}
              >
                <Text
                  {...this.props.DocumentNumber}
                  attribute={`${attribute}.DocumentNumber`}
                  name="DocumentNumber"
                  className="non-us-i94-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
          </div>
        )}
        {this.props.CitizenshipDocumentation?.value === DOC_NonVisa && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.document.visaNumber')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DocumentNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.DocumentNumberNotApplicable}
                name="DocumentNumberNotApplicable"
                attribute={`${attribute}.DocumentNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateDocumentNumberNotApplicable}
              >
                <Text
                  {...this.props.DocumentNumber}
                  attribute={`${attribute}.DocumentNumber`}
                  name="DocumentNumber"
                  className="non-us-visa-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
          </div>
        )}
        {this.props.CitizenshipDocumentation?.value === DOC_NonF1 && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.document.i20Number')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DocumentNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.DocumentNumberNotApplicable}
                name="DocumentNumberNotApplicable"
                attribute={`${attribute}.DocumentNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateDocumentNumberNotApplicable}
              >
                <Text
                  attribute={`${attribute}.DocumentNumber`}
                  name="DocumentNumber"
                  className="non-us-i20-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.DocumentNumber}
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
          </div>
        )}
        {this.props.CitizenshipDocumentation?.value === DOC_NonJ1 && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.relatives.label.document.ds2019Number')
                .split('\n')
                .map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DocumentNumber`}
              exactAttribute
              shrink
            >
              <NotApplicable
                {...this.props.DocumentNumberNotApplicable}
                name="DocumentNumberNotApplicable"
                attribute={`${attribute}.DocumentNumberNotApplicable`}
                label={i18n.t('relationships.pvq.relatives.label.idk')}
                or={i18n.t('relationships.relatives.para.or')}
                onError={this.props.onError}
                onUpdate={this.updateDocumentNumberNotApplicable}
              >
                <Text
                  attribute={`${attribute}.DocumentNumber`}
                  name="DocumentNumber"
                  className="non-us-ds2019-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  {...this.props.DocumentNumber}
                  onError={this.props.onError}
                  onUpdate={this.updateDocumentNumber}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
          </div>
        )}
      </div>
    );
  };

  pvqRelativeEmployedQuestions = (): ReactElement => {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <>
        <Field
          title={i18n.t('relationships.pvq.relatives.heading.employer.occupation')}
          attribute={`${attribute}.RelativeOccupation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.RelativeOccupation`}
            name="RelativeOccupation"
            {...this.props.RelativeOccupation}
            onUpdate={this.updateRelativeOccupation}
            required={this.props.required}
            onError={this.props.onError}
          />
        </Field>
        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.EmployerAddress`}
          adjustFor="address"
        >
          <PVQLocationUnknown
            attribute={`${attribute}.EmployerAddress`}
            name="EmployerAddress"
            {...this.props.EmployerAddress}
            inUsaUnknown={this.props.EmployerAddressNotApplicable}
            inUsaUnknownLabel={i18n.t('reference.label.idk')}
            label={i18n.t('relationships.pvq.relatives.heading.employer.address')}
            layout={PVQLocation.ADDRESS}
            domesticTitle="What is the address of their employer?"
            foreignTitle="Where is their employer located?"
            geocode
            className="relative-employer-address"
            showPostOffice
            onError={this.props.onError}
            onUpdate={this.updateEmployerAddress}
            onUpdateInUsaUnknown={this.updateEmployerAddressNotApplicable}
            required={this.props.required}
            showInUsaUnknown
            showIsInstallationUnknownIfInternational
            showIsInstallationUnknownIfDomestic
            showMilitaryInstallation
            hideInstallationNameUnknownWhenForeign
            showApoFpoUnknown
          />
        </Field>
      </>
    );
  };

  pvqDocumentExpirationQuestions = (): ReactElement => {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <Field
        title={i18n.t('relationships.pvq.relatives.heading.address.expiration')}
        attribute={`${attribute}.Expiration`}
        adjustFor="labels"
        scrollIntoView={this.props.scrollIntoView}
        shrink
      >
        <NotApplicable
          {...this.props.ExpirationNotApplicable}
          name="ExpirationNotApplicable"
          attribute={`${attribute}.ExpirationNotApplicable`}
          label={i18n.t('relationships.pvq.relatives.label.notApplicable')}
          or={i18n.t('relationships.relatives.para.or')}
          onError={this.props.onError}
          onUpdate={this.updateExpirationNotApplicable}
        >
          <NotApplicable
            {...this.props.ExpirationUnknown}
            name="ExpirationUnknown"
            attribute={`${attribute}.ExpirationUnknown`}
            label={i18n.t('relationships.pvq.relatives.label.idk')}
            or={i18n.t('relationships.relatives.para.or')}
            onError={this.props.onError}
            onUpdate={this.updateExpirationUnknown}
            useChangeWarning
          >
            <DateControl
              attribute={`${attribute}.Expiration`}
              name="Expiration"
              className="document-expiration"
              {...this.props.Expiration}
              prefix="relative"
              applicantBirthdate={this.props.Birthdate}
              onError={this.props.onError}
              onUpdate={this.updateExpiration}
              noMaxDate
              required={this.props.required}
            />
          </NotApplicable>
        </NotApplicable>
      </Field>
    );
  };

  render() {
    const {
      requireRelationshipRelativesForeignBornDoc,
      requireRelationshipRelativesUSResidenceDoc,
      requireRelationshipRelativesForeignGovtAffExplanation,
      requireRelationshipRelativesAdditionalFamily,
      formType,
    } = this.props;

    const relativeContactMinDate = pickDate([this.props.applicantBirthdate, this.props.Birthdate]);

    const relativeIsCitizen = isCitizen(this.props);
    const citizenshipDocumentationRequired = requireCitizenshipDocumentation(this.props, {
      requireRelationshipRelativesForeignBornDoc,
      requireRelationshipRelativesUSResidenceDoc,
    });

    const mother = (this.props.Relation || {}).value === 'Mother';
    const father = (this.props.Relation || {}).value === 'Father';
    const child = (this.props.Relation || {}).value === 'Child';
    const immediateFamily = requireRelationshipRelativesAdditionalFamily
      ? immediateAdditionalFamilyOptions.includes(this.props.Relation?.value)
      : immediateFamilyOptions.includes(this.props.Relation?.value);
    const otherMethods = ((this.props.Methods || {}).values || []).some((x) => x === 'Other');
    const otherFrequency = (this.props.Frequency || {}).value === 'Other';
    const attribute = `List.items[${this.props.$index}].Item`;
    const relationType = (this.props.Relation || {}).value;
    const isParentType = ['Mother', 'Father', 'Mother-in-law', 'Father-in-law'].includes(
      relationType
    );

    // create the options for the drop down
    const otherRelatives = this.getOtherRelativeOptions().map((relative, index) => (
      <option key={`${relative.name}_${index}`} value={relative.value}>
        {relative.label}
      </option>
    ));

    // Add inlaw options to dropdown only if applicable
    if (this.props.maritalStatus !== 'NeverMarried') {
      otherRelatives.unshift(
        <option key="relation-motherinlaw" value="Mother-in-law">
          {i18n.t('relationships.relatives.label.relation.motherinlaw')}
        </option>,
        <option key="relation-fatherinlaw" value="Father-in-law">
          {i18n.t('relationships.relatives.label.relation.fatherinlaw')}
        </option>
      );
    }

    // add mother & father to beginning
    otherRelatives.unshift(
      <option key="relation-mother" value="Mother">
        {i18n.t('relationships.relatives.label.relation.mother')}
      </option>,
      <option key="relation-father" value="Father">
        {i18n.t('relationships.relatives.label.relation.father')}
      </option>
    );

    // add in guardian
    otherRelatives.push(
      <option key="relation-guardian" value="Guardian">
        {i18n.t('relationships.relatives.label.relation.guardian')}
      </option>
    );

    return isPVQForm(formType) ? (
      <div className="relative-item">
        <Field
          attribute={`${attribute}.PvqRelation`}
          title={i18n.t('relationships.pvq.relatives.heading.relation')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="big-buttons"
        >
          <Dropdown
            attribute={`${attribute}.PvqRelation`}
            placeholder={this.props.PvqRelation?.value}
            className="relative-pvq-relation"
            onUpdate={this.updatePvqRelation}
            onError={this.props.onError}
            sort={false}
            value={this.props.PvqRelation?.value}
            required={this.props.required}
          >
            {relativeTypes.map((relativeType) => (
              <option
                key={relativeType}
                value={relativeType}
              >
                {relativeType}
              </option>
            ))}

          </Dropdown>
        </Field>

        <Field
          title={i18n.t('relationships.pvq.relatives.heading.name')}
          attribute={`${attribute}.Name`}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.Name}
            attribute={`${attribute}.Name`}
            name="Name"
            className="relative-name"
            aria-label="Provide your relative's full name"
            onError={this.handleNameError}
            onUpdate={(value: RelationshipsRelativeItemModel['Name']) => {
              this.updateField('Name', value);
            }}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}

          />
        </Field>

        <Checkbox
          {...this.props.NoContactWithFamily}
          name="NoContactWithFamily"
          onUpdate={this.updateNoContactWithFamily}
          onError={this.props.onError}
          label={i18n.t('relationships.pvq.relatives.label.noContact')}
          toggle="false"
          attribute={`${attribute}.NoContactWithFamily`}
        />

        <Field
          attribute={`${attribute}.Birthdate`}
          title={i18n.t('relationships.pvq.relatives.heading.birthdate')}
          help="relationships.relatives.help.birthdate"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          shrink
        >
          <NotApplicable
            name="BirthdateNotApplicable"
            {...this.props.BirthdateNotApplicable}
            label={i18n.t('relationships.relatives.label.idk')}
            or={i18n.t('relationships.relatives.para.or')}
            onUpdate={this.updateBirthdateNotApplicable}
            attribute={`${attribute}.BirthdateNotApplicable`}
          >
            <DateControl
              {...this.props.Birthdate}
              name="Birthdate"
              attribute={`${attribute}.Birthdate`}
              className="relative-birthdate"
              prefix={mother || father ? 'parent.dob' : child ? 'child.dob' : 'dob'} // eslint-disable-line no-nested-ternary
              relationship={this.props.Relation?.value ?? 'None'}
              onError={this.props.onError}
              maxDate={mother || father ? this.props.applicantBirthdate : ''}
              onUpdate={(value: RelationshipsRelativeItemModel['Birthdate']) => {
                this.updateField('Birthdate', value);
              }}
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Field
          attribute={`${attribute}.Birthplace`}
          title={i18n.t('relationships.pvq.relatives.heading.birthplace')}
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          validate={false}
        >
          <PVQLocationUnknown
            name="Birthplace"
            attribute={`${attribute}.Birthplace`}
            addressUnknown={this.props.TrusteeAddressNotApplicable}
            addressUnknownLabel={i18n.t('relationships.relatives.label.idk')}
            onUpdateAddressUnknown={this.updateBirthplaceNotApplicable}
            {...this.props.Birthplace}
            layout={PVQLocation.IDENTIFICATION_BIRTH_PLACE}
            stateLabel={i18n.t('identification.pvq.birthplace.label.state')}
            cityLabel={i18n.t('identification.pvq.birthplace.label.city')}
            countyLabel={i18n.t('identification.pvq.birthplace.label.county')}
            countryLabel={i18n.t('identification.pvq.birthplace.label.country')}
            className="relative-birthplace"
            onError={this.props.onError}
            onUpdate={this.updateBirthPlacePVQ}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Citizenship`}
          title={i18n.t('relationships.pvq.relatives.heading.citizenship')}
          scrollIntoView={this.props.scrollIntoView}
          help="relationships.relatives.help.citizenship"
          exactAttribute
        >
          <NotApplicable
            name="citizenship-not-applicable"
            {...this.props.CitizenshipCountryNotApplicable}
            label={i18n.t('reference.label.idk')}
            onUpdate={this.updateCitizenshipCountryNotApplicable}
            attribute={`${attribute}.CitizenshipCountryNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Citizenship?.value)} // eslint-disable-line @typescript-eslint/no-unsafe-member-access
          >
            <Country
              {...this.props.Citizenship}
              name="Citizenship"
              attribute={`${attribute}.Citizenship`}
              multiple
              className="relative-citizenship"
              onError={this.props.onError}
              onUpdate={this.updateCitizenship}
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Branch
          {...this.props.IsDeceased}
          name="is_deceased"
          attribute={`${attribute}.IsDeceased`}
          label={i18n.t('relationships.pvq.relatives.heading.deceased.branch')}
          labelSize="h4"
          className="relative-deceased"
          onUpdate={(value: RelationshipsRelativeItemModel['IsDeceased']) => {
            this.updateField('IsDeceased', value);
          }}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.props.onError}
          showDNK
        />
        {(this.props.IsDeceased || {}).value === 'No' && this.pvqNotDeceasedFormQuestions()}
      </div>
    ) : ( // SF86
      <div className="relative-item">
        <Field
          attribute={`${attribute}.Relation`}
          title={i18n.t('relationships.relatives.heading.relation')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="big-buttons"
        >
          <Dropdown
            attribute={`${attribute}.Relation`}
            placeholder={this.props.Relation.value}
            className="relative-relation"
            onUpdate={(value: RelationshipsRelativeItemModel['Relation']) => {
              this.updateParentRelation(value);
            }}
            onError={this.props.onError}
            sort={false}
            value={this.props.Relation.value}
            required={this.props.required}
          >
            {otherRelatives}
          </Dropdown>
          {isParentType
            && (
              <NotApplicable
                {...this.props.ParentNotApplicable}
                name="ParentNotApplicable"
                attribute={`${attribute}.ParentNotApplicable`}
                label={i18n.t('relationships.relatives.label.idkRelative')}
                onError={this.props.onError}
                onUpdate={(value: RelationshipsRelativeItemModel['Relation']) => {
                  this.updateParentNotApplicableAndPrefills(value, relationType);
                }}
                useChangeWarning
              />
            )}
          {isParentType && (this.props.ParentNotApplicable || {}).applicable === false
            && (
              <Field
                title={i18n.t('relationships.relatives.heading.relativeCommentsTitle')}
                attribute={`${attribute}.EntryExplanation`}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  attribute={`${attribute}.EntryExplanation`}
                  name="EntryExplanation"
                  {...this.props.EntryExplanation}
                  onUpdate={(value: RelationshipsRelativeItemModel['EntryExplanation']) => {
                    this.updateField('EntryExplanation', value);
                  }}
                  required={this.props.required}
                  onError={this.props.onError}
                />
              </Field>
            )}
        </Field>
        {this.props.ParentNotApplicable?.applicable !== false
          && (
            <>
              <Field
                title={i18n.t('relationships.relatives.heading.name')}
                attribute={`${attribute}.Name`}
                optional
                filterErrors={Name.requiredErrorsOnly}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Name
                  attribute={`${attribute}.Name`}
                  name="Name"
                  className="relative-name"
                  aria-label="Provide your relative's full name"
                  {...this.props.Name}
                  onError={this.handleNameError}
                  onUpdate={(value: RelationshipsRelativeItemModel['Name']) => {
                    this.updateField('Name', value);
                  }}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                />
              </Field>

              <Field
                attribute={`${attribute}.Birthdate`}
                title={i18n.t('relationships.relatives.heading.birthdate')}
                help="relationships.relatives.help.birthdate"
                adjustFor="labels"
                scrollIntoView={this.props.scrollIntoView}
                shrink
              >
                <DateControl
                  name="Birthdate"
                  attribute={`${attribute}.Birthdate`}
                  className="relative-birthdate"
                  {...this.props.Birthdate}
                  prefix={mother || father ? 'parent.dob' : child ? 'child.dob' : 'dob'} // eslint-disable-line no-nested-ternary
                  relationship={this.props.Relation?.value ?? 'None'}
                  onError={this.props.onError}
                  maxDate={mother || father ? this.props.applicantBirthdate : ''}
                  onUpdate={(value: RelationshipsRelativeItemModel['Birthdate']) => {
                    this.updateField('Birthdate', value);
                  }}
                  required={this.props.required}
                />
              </Field>

              <Field
                attribute={`${attribute}.Birthplace`}
                title={i18n.t('relationships.relatives.heading.birthplace')}
                adjustFor="label"
                scrollIntoView={this.props.scrollIntoView}
                validate={false}
              >
                <Location
                  name="Birthplace"
                  attribute={`${attribute}.Birthplace`}
                  {...this.props.Birthplace}
                  label={i18n.t('relationships.relatives.label.birthplace')}
                  layout={Location.BIRTHPLACE_WITHOUT_COUNTY}
                  help=""
                  hideCounty
                  className="relative-birthplace"
                  onError={this.props.onError}
                  onUpdate={this.updateBirthPlace}
                  required={this.props.required}
                />
              </Field>

              <Field
                attribute={`${attribute}.Citizenship`}
                title={i18n.t('relationships.relatives.heading.citizenship')}
                scrollIntoView={this.props.scrollIntoView}
                help="relationships.relatives.help.citizenship"
                exactAttribute
              >
                <Country
                  name="Citizenship"
                  attribute={`${attribute}.Citizenship`}
                  multiple
                  {...this.props.Citizenship}
                  className="relative-citizenship"
                  onError={this.props.onError}
                  onUpdate={this.updateCitizenship}
                  required={this.props.required}
                />
              </Field>
              <Comments
                title={i18n.t('relationships.relatives.heading.optionalComments.title')}
                attribute={`${attribute}.EntryComments`}
                name="Comments"
                className="relative-optional-comments"
                {...this.props.EntryComments}
                onUpdate={(value: RelationshipsRelativeItemModel['EntryComments']) => {
                  this.updateField('EntryComments', value);
                }}
              />

              {mother
                && (
                  <div>
                    <Branch
                      name="maiden_diff"
                      label={i18n.t('relationships.relatives.heading.maiden')}
                      labelSize="h4"
                      attribute={`${attribute}.MaidenSameAsListed`}
                      className="eapp-field-wrap relative-maiden-diff"
                      {...this.props.MaidenSameAsListed}
                      yesLabel={i18n.t('relationships.relatives.label.maiden.same')}
                      noLabel={i18n.t('relationships.relatives.label.maiden.diff')}
                      onUpdate={(value: RelationshipsRelativeItemModel['MaidenSameAsListed']) => {
                        this.updateField('MaidenSameAsListed', value);
                      }}
                      required={this.props.required}
                      scrollIntoView={this.props.scrollIntoView}
                      onError={this.props.onError}
                    />
                    <Comments
                      title={i18n.t('relationships.comments.optional')}
                      name="MothersMaidenNameComments"
                      className="optional-comment"
                      attribute="MothersMaidenNameComments"
                      {...this.props.MothersMaidenNameComments}
                      onUpdate={this.updateMothersMaidenNameComments}
                      required={false}
                    />
                    {this.props.MaidenSameAsListed?.value === 'No'
                      && (
                        <Field
                          attribute={`${attribute}.MaidenName`}
                          optional
                          filterErrors={Name.requiredErrorsOnly}
                          scrollIntoView={this.props.scrollIntoView}
                        >
                          <Name
                            attribute={`${attribute}.MaidenName`}
                            name="MaidenName"
                            className="relative-maidenname eapp-field-wrap"
                            aria-label="Provide your relative's maiden name"
                            {...this.props.MaidenName}
                            onError={this.handleNameError}
                            onUpdate={(value: RelationshipsRelativeItemModel['MaidenName']) => {
                              this.updateField('MaidenName', value);
                            }}
                            required={this.props.required}
                            scrollIntoView={this.props.scrollIntoView}
                          />
                        </Field>
                      )}
                  </div>
                )}

              {immediateFamily
                && (
                  <>
                    <BranchCollection
                      {...this.props.Aliases}
                      attribute={`${attribute}.Aliases`}
                      branchName="has_alias"
                      label={i18n.t('relationships.relatives.heading.alias.branch')}
                      appendLabel={i18n.t('relationships.relatives.heading.alias.branch')}
                      className="relative-alias"
                      onUpdate={(value: RelationshipsRelativeItemModel['Aliases']) => {
                        this.updateField('Aliases', value);
                      }}
                      onError={this.props.onError}
                      required={this.props.required}
                      scrollIntoView={this.props.scrollIntoView}
                      branchComment={this.branchComment}
                    >
                      <div>
                        <Alias
                          name="Item"
                          attribute={`${attribute}.Aliases`}
                          minDate={this.props.Birthdate}
                          relationship="Other"
                          onError={this.props.onError}
                          hideMaiden={mother && this.props.MaidenSameAsListed?.value === 'Yes'}
                          required={this.props.required}
                          scrollIntoView={this.props.scrollIntoView}
                          bind
                        />
                      </div>
                    </BranchCollection>
                    {this.props.Aliases?.items?.[0]?.Item?.Has?.value === 'Yes' && (
                      <Comments
                        title={i18n.t('relationships.comments.summary')}
                        name="AliasesSummaryComments"
                        attribute="AliasesSummaryComments"
                        className="summary-comment"
                        {...this.props.AliasesSummaryComments}
                        onUpdate={this.updateAliasesSummaryComments}
                        onError={this.props.onError}
                        required={false}
                      />
                    )}
                  </>
                )}

              <Branch
                name="is_deceased"
                attribute={`${attribute}.IsDeceased`}
                label={i18n.t('relationships.relatives.heading.deceased.branch')}
                labelSize="h4"
                className="relative-deceased"
                {...this.props.IsDeceased}
                onUpdate={(value: RelationshipsRelativeItemModel['IsDeceased']) => {
                  this.updateField('IsDeceased', value);
                }}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
              />
              <Comments
                title={i18n.t('relationships.comments.optional')}
                name="DeceasedComments"
                className="optional-comment"
                attribute="DeceasedComments"
                {...this.props.DeceasedComments}
                onUpdate={this.updateDeceasedComments}
                required={false}
              />
              {(this.props.IsDeceased || {}).value === 'No'
                && (
                  <>
                    <Field
                      title={i18n.t('relationships.relatives.heading.deceased.address')}
                      attribute={`${attribute}.Address`}
                      optional
                      help="relationships.relatives.help.address"
                      scrollIntoView={this.props.scrollIntoView}
                      adjustFor="address"
                      exactAttribute
                    >
                      <NotApplicable
                        name="AddressNotApplicable"
                        {...this.props.AddressNotApplicable}
                        label={i18n.t('relationships.relatives.label.idk')}
                        or={i18n.t('relationships.relatives.para.or')}
                        onUpdate={this.updateAddressNotApplicable}
                        attribute="AddressNotApplicable"
                        includeNAConfirmation={Boolean(this.props.Address
                          && (this.props.Address.city
                            || this.props.Address.state
                          ))}
                      >
                        <Location
                          attribute={`${attribute}.Address`}
                          name="Address"
                          className="relative-address"
                          {...this.props.Address}
                          addressBooks={this.props.addressBooks}
                          addressBook={this.props.addressBook}
                          dispatch={this.props.dispatch}
                          layout={Location.ADDRESS}
                          geocode
                          onUpdate={(value: RelationshipsRelativeItemModel['Address']) => {
                            this.updateAddressField(value);
                          }}
                          onError={this.props.onError}
                          required={this.props.required}
                        />
                      </NotApplicable>
                    </Field>
                    {(this.props.AddressNotApplicable || {}).applicable === false
                      && (
                        <Field
                          title={i18n.t('relationships.relatives.heading.addressExplanation')}
                          attribute={`${attribute}.AddressExplanation`}
                          scrollIntoView={this.props.scrollIntoView}
                        >
                          <Textarea
                            attribute={`${attribute}.AddressExplanation`}
                            name="AddressExplanation"
                            {...this.props.AddressExplanation}
                            onUpdate={(value: RelationshipsRelativeItemModel['AddressExplanation']) => {
                              this.updateField('AddressExplanation', value);
                            }}
                            required={this.props.required}
                            onError={this.props.onError}
                          />
                        </Field>
                      )}

                    {(this.props.Address
                      && this.props.Address.country
                      && countryString(this.props.Address.country || {}) === 'United States')
                      && (
                        <Comments
                          title={i18n.t('relationships.comments.optional')}
                          name={`${attribute}AddressComments`}
                          className="optional-comment"
                          attribute={`${attribute}AddressComments`}
                          {...this.props.AddressComments}
                          onUpdate={this.updateAddressComments}
                          required={false}
                        />
                      )}
                    {(this.props.Address
                      && this.props.Address.country
                      && countryString(this.props.Address.country || {}) !== 'United States')
                      && (
                        <ConnectedAlternateAddress
                          attribute={`${attribute}.AlternateAddress`}
                          address={this.props.AlternateAddress}
                          addressBook={this.props.addressBook}
                          addressNA={this.props.AlternateAddressNotApplicable}
                          belongingTo="AlternateAddress"
                          belongingToNA="AlternateAddressNotApplicable"
                          country={this.props.Address?.country}
                          disablePhysical
                          forceAPO
                          militaryAddressLabel={i18n.t('address.militaryAddress.relative')}
                          onUpdate={this.update}
                          required={this.props.required}
                          onError={this.props.onError}
                          showDNK={this.props.requireRelationshipRelativesAPOFPOIDK}
                          AnswerComments={this.props.AnswerComments}
                          AddressComments={this.props.AddressComments}
                          PhysicalLocationComments={this.props.PhysicalLocationComments}
                        />
                      )}
                    {/*
                    HasDifferentAddress comes from the AlternateAddress component and corresponds to the "I Don't Know" answer of the
                    "Does this relative have an APO/FPO address?" question.
                    HasDifferentAddress uses a new type: YesNoIDontKnowModel, it is used to store "Yes", "No", and "IDontKnow".
                    The following checks are all necessary because the explanation box shows up before its time otherwise:
                    relative not deceased, country outside the U.S., and I Don't Know for APO address.
                    */}
                    {this.props.AlternateAddress?.HasDifferentAddress?.value === 'IDontKnow'
                      && this.props.Address?.country
                      && countryString(this.props.Address.country) !== 'United States'
                      && this.props.IsDeceased?.value === 'No' && (
                        <Field // Double check that this should not be comments
                          title={i18n.t('relationships.relatives.heading.relativeCommentsTitle')}
                          attribute={`${attribute}.AlternateAddressExplanation`}
                          scrollIntoView={this.props.scrollIntoView}
                        >
                          <Textarea
                            attribute={`${attribute}.AlternateAddressExplanation`}
                            name="AlternateAddressExplanation"
                            className="alternate-address-dnk-comments"
                            {...this.props.AlternateAddressExplanation}
                            onUpdate={this.updateAlternateAddressExplanation}
                            required={this.props.required}
                            onError={this.props.onError}
                          />
                        </Field>
                    )}
                  </>
                )}

              {citizenshipDocumentationRequired
                && (
                  <div>
                    <Field
                      title={i18n.t('relationships.relatives.heading.us.title')}
                      attribute={`${attribute}.CitizenshipDocumentation`}
                      titleSize="h4"
                      scrollIntoView={this.props.scrollIntoView}
                    >
                      <Field
                        title={i18n.t('relationships.relatives.heading.us.documentation')}
                        titleSize="label"
                        attribute={`${attribute}.CitizenshipDocumentation`}
                        className="relative-citizenship-documentation no-margin-bottom"
                      />

                      <label id="abroad">{i18n.t('relationships.relatives.para.abroad')}</label>
                      <RadioGroup
                        className="relative-abroad option-list option-list-vertical"
                        required={this.props.required}
                        onError={this.props.onError}
                        attribute={`${attribute}.CitizenshipDocumentation`}
                        selectedValue={(this.props.CitizenshipDocumentation || {}).value}
                      >
                        <Radio
                          name="abroad-fs"
                          label={i18n.t('relationships.relatives.label.abroad.fs')}
                          value="FS"
                          className="abroad-fs"
                          onError={this.props.onError}
                          onUpdate={this.updateCitizenshipDocumentation}
                        />
                        <Radio
                          name="abroad-ds"
                          label={i18n.t('relationships.relatives.label.abroad.ds')}
                          value="DS"
                          className="abroad-ds"
                          onError={this.props.onError}
                          onUpdate={this.updateCitizenshipDocumentation}
                        />
                      </RadioGroup>

                      <label id="naturalized">{i18n.t('relationships.relatives.para.naturalized')}</label>
                      <RadioGroup
                        className="relative-naturalized option-list option-list-vertical"
                        required={this.props.required}
                        onError={this.props.onError}
                        attribute={`${attribute}.CitizenshipDocumentation`}
                        selectedValue={(this.props.CitizenshipDocumentation || {}).value}
                      >
                        <Radio
                          name="naturalized-alien"
                          label={i18n.t('relationships.relatives.label.naturalized.alien')}
                          value="NaturalizedAlien"
                          className="naturalized-alien"
                          onError={this.props.onError}
                          onUpdate={this.updateCitizenshipDocumentation}
                        />
                        <Radio
                          name="naturalized-permanent"
                          label={i18n.t('relationships.relatives.label.naturalized.permanent')}
                          value="NaturalizedPermanent"
                          className="naturalized-permanent"
                          onError={this.props.onError}
                          onUpdate={this.updateCitizenshipDocumentation}
                        />
                        <Radio
                          name="naturalized-certificate"
                          label={i18n.t('relationships.relatives.label.naturalized.certificate')}
                          value="NaturalizedCertificate"
                          className="naturalized-certificate"
                          onUpdate={this.updateCitizenshipDocumentation}
                        />
                      </RadioGroup>

                      <label id="relative-derived">
                        {i18n.t('relationships.relatives.para.derived')}
                      </label>
                      <RadioGroup
                        className="relative-derived option-list option-list-vertical"
                        required={this.props.required}
                        onError={this.props.onError}
                        attribute={`${attribute}.CitizenshipDocumentation`}
                        selectedValue={(this.props.CitizenshipDocumentation || {}).value}
                      >
                        {this.getCitizenshipDocumentation().map((document) => (
                          <Radio
                            key={document.name}
                            name={document.name}
                            label={document.label}
                            value={document.value}
                            className={document.name}
                            onError={this.props.onError}
                            onUpdate={this.updateCitizenshipDocumentation}
                          />
                        ))}
                      </RadioGroup>
                      {this.props.CitizenshipDocumentation?.value === 'Other' && (
                        <Field attribute={`${attribute}.OtherCitizenshipDocumentation`}>
                          <Textarea
                            name="OtherCitizenshipDocumentation"
                            attribute={`${attribute}.OtherCitizenshipDocumentation`}
                            className="derived-other-explanation"
                            {...this.props.OtherCitizenshipDocumentation}
                            onError={this.props.onError}
                            onUpdate={(
                              value: RelationshipsRelativeItemModel['OtherCitizenshipDocumentation']
                            ) => {
                              this.updateField('OtherCitizenshipDocumentation', value);
                            }}
                            required={this.props.required}
                          />
                        </Field>
                      )}
                    </Field>
                    <Field
                      title={i18n.t('relationships.relatives.heading.us.number')}
                      adjustFor="buttons"
                      scrollIntoView={this.props.scrollIntoView}
                      attribute={`${attribute}.DocumentNumber`}
                      exactAttribute
                      shrink
                    >
                      <Text
                        attribute={`${attribute}.DocumentNumber`}
                        name="DocumentNumber"
                        className="relative-documentnumber"
                        maxlength="30"
                        pattern={alphaNumericRegEx}
                        prefix="alphanumeric"
                        {...this.props.DocumentNumber}
                        onError={this.props.onError}
                        onUpdate={(value: RelationshipsRelativeItemModel['DocumentNumber']) => {
                          this.updateDocumentNumber(value);
                        }}
                        required={this.props.required}
                      />
                    </Field>
                    <Field
                      title={i18n.t('relationships.relatives.heading.us.name')}
                      attribute={`${attribute}.CourtName`}
                      scrollIntoView={this.props.scrollIntoView}
                    >
                      <Text
                        attribute={`${attribute}.CourtName`}
                        name="CourtName"
                        className="relative-courtname"
                        {...this.props.CourtName}
                        onError={this.props.onError}
                        onUpdate={(value: RelationshipsRelativeItemModel['CourtName']) => {
                          this.updateField('CourtName', value);
                        }}
                        required={this.props.required}
                      />
                    </Field>

                    <Field
                      title={i18n.t('relationships.relatives.heading.us.address')}
                      attribute={`${attribute}.CourtAddress`}
                      optional
                      scrollIntoView={this.props.scrollIntoView}
                      help="relationships.relatives.help.courtaddress"
                      adjustFor="labels"
                      exactAttribute
                    >
                      <NotApplicable
                        name="CourtAddressNotApplicable"
                        {...this.props.CourtAddressNotApplicable}
                        label={i18n.t('relationships.relatives.label.idk')}
                        or={i18n.t('relationships.relatives.para.or')}
                        onUpdate={this.updateCourtAddressNotApplicable}
                        attribute="CourtAddressNotApplicable"
                        includeNAConfirmation={Boolean(this.props.CourtAddress
                          && (this.props.CourtAddress.city
                            || this.props.CourtAddress.state
                          ))}
                      >
                        <Location
                          attribute={`${attribute}.CourtAddress`}
                          name="CourtAddress"
                          {...this.props.CourtAddress}
                          layout={Location.US_ADDRESS}
                          geocode
                          className="relative-courtaddress"
                          onError={this.props.onError}
                          onUpdate={(value: RelationshipsRelativeItemModel['CourtAddress']) => {
                            this.updateField('CourtAddress', value);
                          }}
                          required={this.props.required}
                        />
                      </NotApplicable>
                    </Field>
                    {(this.props.CourtAddressNotApplicable || {}).applicable === false
                      && (
                        <Field
                          title={i18n.t('relationships.relatives.heading.courtAddressExplanation')}
                          attribute={`${attribute}.CourtAddressExplanation`}
                          scrollIntoView={this.props.scrollIntoView}
                        >
                          <Textarea
                            attribute={`${attribute}.CourtAddressExplanation`}
                            name="CourtAddressExplanation"
                            {...this.props.CourtAddressExplanation}
                            onUpdate={(value: RelationshipsRelativeItemModel['CourtAddressExplanation']) => {
                              this.updateField('CourtAddressExplanation', value);
                            }}
                            required={this.props.required}
                            onError={this.props.onError}
                          />
                        </Field>
                      )}
                    <Comments
                      title={i18n.t('relationships.comments.optional')}
                      name="OptionalComments"
                      className="optional-comment"
                      attribute="OptionalComments"
                      {...this.props.OptionalComments}
                      onUpdate={this.updateOptionalComments}
                      required={false}
                    />
                  </div>
                )}

              {requireRelationshipRelativesUSResidenceDoc
                && this.props.Citizenship.value
                && !relativeIsCitizen
                && this.props.IsDeceased.value === 'No'
                && (
                  <div>
                    {this.props.Address
                      && countryString(this.props.Address.country || {}) === 'United States'
                      && (
                        <div>
                          <Field
                            title={i18n.t('relationships.relatives.heading.address.title')}
                            attribute={`${attribute}.Document`}
                            comments={false}
                            scrollIntoView={this.props.scrollIntoView}
                            adjustFor="big-buttons"
                          >
                            <div id="relative-document">
                              {i18n.t('relationships.relatives.para.notcitizen')}
                              <RadioGroup
                                className="relative-document option-list option-list-vertical"
                                attribute={`${attribute}.Document`}
                                required={this.props.required}
                                onError={this.props.onError}
                                selectedValue={(this.props.Document || {}).value}
                              >
                                {this.getNonCitizenDocumentation().map((document) => (
                                  <Radio
                                    key={document.name}
                                    name={document.name}
                                    label={document.label}
                                    value={document.value}
                                    className={document.name}
                                    onError={this.props.onError}
                                    onUpdate={this.updateDocument}
                                  />
                                ))}
                              </RadioGroup>

                              {this.props.Document?.value === 'Other' && (
                                <Textarea // Double check whether this can be a comments component
                                  attribute={`${attribute}.DocumentComments`}
                                  name="DocumentComments"
                                  className="relative-document-other-comments"
                                  {...this.props.DocumentComments}
                                  onUpdate={(value: RelationshipsRelativeItemModel['DocumentComments']) => {
                                    this.updateField('DocumentComments', value);
                                  }}
                                  required={this.props.required}
                                />
                              )}
                            </div>
                          </Field>

                          <Field
                            title={i18n.t('relationships.relatives.heading.address.number')}
                            adjustFor="buttons"
                            scrollIntoView={this.props.scrollIntoView}
                            attribute={`${attribute}.ResidenceDocumentNumber`}
                            exactAttribute
                            shrink
                          >
                            <NotApplicable
                              {...this.props.DocumentNumberNotApplicable}
                              name="DocumentNumberNotApplicable"
                              attribute={`${attribute}.DocumentNumberNotApplicable`}
                              label={i18n.t('relationships.relatives.label.idk')}
                              or={i18n.t('relationships.relatives.para.or')}
                              onError={this.props.onError}
                              onUpdate={this.updateDocumentNumberNotApplicable}
                            >
                              <Text
                                attribute={`${attribute}.ResidenceDocumentNumber`}
                                name="ResidenceDocumentNumber"
                                className="relative-residence-documentnumber"
                                maxlength="30"
                                pattern={alphaNumericRegEx}
                                prefix="alphanumeric"
                                {...this.props.ResidenceDocumentNumber}
                                onError={this.props.onError}
                                onUpdate={(
                                  value: RelationshipsRelativeItemModel['ResidenceDocumentNumber']
                                ) => {
                                  this.updateResidenceDocumentNumber(value);
                                }}
                                required={this.props.required}
                              />
                            </NotApplicable>
                          </Field>
                          {this.props.DocumentNumberNotApplicable?.applicable === false
                            && this.props.Document?.value !== 'Other' && (
                              <Field // Double Check
                                title={i18n.t('relationships.relatives.heading.documentnumberexplanation')}
                                attribute={`${attribute}.DocumentNumberExplanation`}
                                scrollIntoView={this.props.scrollIntoView}
                              >
                                <Textarea
                                  attribute={`${attribute}.DocumentNumberExplanation`}
                                  name="DocumentNumberExplanation"
                                  className="document-number-dnk-comments"
                                  {...this.props.DocumentNumberExplanation}
                                  onUpdate={this.updateDocumentNumberExplanation}
                                  required={this.props.required}
                                  onError={this.props.onError}
                                />
                              </Field>
                          )}
                          {this.documentExpirationRequired(this.props.Document?.value) && (
                            <Field
                              title={i18n.t('relationships.relatives.heading.address.expiration')}
                              attribute={`${attribute}.Expiration`}
                              adjustFor="labels"
                              scrollIntoView={this.props.scrollIntoView}
                              shrink
                            >
                              <DateControl
                                attribute={`${attribute}.Expiration`}
                                name="Expiration"
                                className="relative-expiration"
                                {...this.props.Expiration}
                                prefix="relative"
                                applicantBirthdate={this.props.Birthdate}
                                onError={this.props.onError}
                                onUpdate={(value: RelationshipsRelativeItemModel['Expiration']) => {
                                  this.updateField('Expiration', value);
                                }}
                                noMaxDate
                                required={this.props.required}
                              />
                            </Field>
                          )}
                        </div>
                      )}

                    <Field
                      title={i18n.t('relationships.relatives.heading.address.firstcontact')}
                      attribute={`${attribute}.FirstContact`}
                      help="relationships.relatives.help.firstcontact"
                      adjustFor="labels"
                      scrollIntoView={this.props.scrollIntoView}
                      shrink
                      exactAttribute
                    >
                      <NotApplicable
                        name="FirstContactNotApplicable"
                        {...this.props.FirstContactNotApplicable}
                        label={i18n.t('relationships.relatives.label.idk')}
                        or={i18n.t('relationships.relatives.para.or')}
                        onUpdate={this.updateFirstContactNotApplicable}
                        attribute="FirstContactNotApplicable"
                        includeNAConfirmation={Boolean(this.props.FirstContact
                          && (this.props.FirstContact.month
                            || this.props.FirstContact.year
                          ))}
                      >
                        <DateControl
                          attribute={`${attribute}.FirstContact`}
                          name="FirstContact"
                          className="relative-first-contact"
                          {...this.props.FirstContact}
                          minDate={relativeContactMinDate}
                          minDateEqualTo
                          onError={this.props.onError}
                          onUpdate={(value: RelationshipsRelativeItemModel['FirstContact']) => {
                            this.updateField('FirstContact', value);
                          }}
                          required={this.props.required}
                          hideDay
                        />
                      </NotApplicable>
                    </Field>
                    {(this.props.FirstContactNotApplicable || {}).applicable === false
                      && (
                        <Field
                          title={i18n.t('relationships.relatives.heading.firstContactExplanation')}
                          attribute={`${attribute}.FirstContactExplanation`}
                          scrollIntoView={this.props.scrollIntoView}
                        >
                          <Textarea
                            attribute={`${attribute}.FirstContactExplanation`}
                            name="FirstContactExplanation"
                            {...this.props.FirstContactExplanation}
                            onUpdate={(value: RelationshipsRelativeItemModel['FirstContactExplanation']) => {
                              this.updateField('FirstContactExplanation', value);
                            }}
                            required={this.props.required}
                            onError={this.props.onError}
                          />
                        </Field>
                      )}

                    <Field
                      title={i18n.t('relationships.relatives.heading.address.lastcontact')}
                      attribute={`${attribute}.LastContact`}
                      help="relationships.relatives.help.lastcontact"
                      adjustFor="labels"
                      scrollIntoView={this.props.scrollIntoView}
                      shrink
                    >
                      <DateControl
                        attribute={`${attribute}.LastContact`}
                        name="LastContact"
                        className="relative-last-contact"
                        {...this.props.LastContact}
                        prefix="relative"
                        minDate={this.props.FirstContact}
                        minDateEqualTo
                        onError={this.props.onError}
                        onUpdate={(value: RelationshipsRelativeItemModel['LastContact']) => {
                          this.updateField('LastContact', value);
                        }}
                        required={this.props.required}
                        hideDay
                      />
                    </Field>

                    <Field
                      attribute={`${attribute}.Methods`}
                      title={i18n.t('relationships.relatives.heading.address.methods')}
                      comments={otherMethods}
                      commentsName="MethodsComments"
                      commentsValue={this.props.MethodsComments}
                      commentsActive={otherMethods}
                      commentsRequired={otherMethods}
                      commentAttribute={`${attribute}.MethodsComments`}
                      onUpdate={(value: RelationshipsRelativeItemModel['MethodsComments']) => {
                        this.updateField('MethodsComments', value);
                      }}
                      onError={this.props.onError}
                      adjustFor="big-buttons"
                      scrollIntoView={this.props.scrollIntoView}
                    >
                      <div>
                        {i18n.m('relationships.relatives.para.checkall')}
                        <CheckboxGroup
                          attribute={`${attribute}.Methods`}
                          className="relative-methods option-list option-list-vertical"
                          required={this.props.required}
                          onError={this.props.onError}
                          selectedValues={(this.props.Methods || {}).values}
                        >
                          <Checkbox
                            name="methods-inperson"
                            label={i18n.t('relationships.relatives.label.methods.inperson')}
                            value="In person"
                            className="methods-inperson"
                            onError={this.props.onError}
                            onUpdate={this.updateMethods}
                          />
                          <Checkbox
                            name="methods-telephone"
                            label={i18n.t('relationships.relatives.label.methods.telephone')}
                            value="Telephone"
                            className="methods-telephone"
                            onError={this.props.onError}
                            onUpdate={this.updateMethods}
                          />
                          <Checkbox
                            name="methods-electronic"
                            label={i18n.t('relationships.relatives.label.methods.electronic')}
                            value="Electronic"
                            className="methods-electronic"
                            onError={this.props.onError}
                            onUpdate={this.updateMethods}
                          />
                          <Checkbox
                            name="methods-written"
                            label={i18n.t('relationships.relatives.label.methods.written')}
                            value="Written"
                            className="methods-written"
                            onError={this.props.onError}
                            onUpdate={this.updateMethods}
                          />
                          <Checkbox
                            name="methods-other"
                            label={i18n.t('relationships.relatives.label.methods.other')}
                            value="Other"
                            className="methods-other"
                            onError={this.props.onError}
                            onUpdate={this.updateMethods}
                          />
                        </CheckboxGroup>
                      </div>
                    </Field>

                    <Field
                      attribute={`${attribute}.Frequency`}
                      title={i18n.t('relationships.relatives.heading.address.frequency')}
                      comments={otherFrequency}
                      commentsName="FrequencyComments"
                      commentsValue={this.props.FrequencyComments}
                      commentsActive={otherFrequency}
                      commentsRequired={otherFrequency}
                      commentAttribute={`${attribute}.FrequencyComments`}
                      onUpdate={(value: RelationshipsRelativeItemModel['FrequencyComments']) => {
                        this.updateField('FrequencyComments', value);
                      }}
                      onError={this.props.onError}
                      scrollIntoView={this.props.scrollIntoView}
                      adjustFor="big-buttons"
                    >
                      <RadioGroup
                        className="relative-frequency option-list option-list-vertical"
                        attribute={`${attribute}.Frequency`}
                        required={this.props.required}
                        onError={this.props.onError}
                        selectedValue={this.props.Frequency?.value}
                      >
                        {this.getFrequency().map((freq) => (
                          <Radio
                            key={freq.name}
                            name={freq.name}
                            label={freq.label}
                            value={freq.value}
                            className={freq.name}
                            onError={this.props.onError}
                            onUpdate={this.updateFrequency}
                          />
                        ))}
                      </RadioGroup>
                    </Field>
                    <Comments
                      title={i18n.t('relationships.comments.optional')}
                      name="ForeignRelativeComments"
                      className="optional-comment"
                      attribute="ForeignRelativeComments"
                      {...this.props.ForeignRelativeComments}
                      onUpdate={this.updateForeignRelativeComments}
                      required={false}
                    />

                    <Field
                      title={i18n.t('relationships.relatives.heading.employer.name')}
                      adjustFor="buttons"
                      scrollIntoView={this.props.scrollIntoView}
                      attribute={`${attribute}.Employer`}
                      exactAttribute
                      shrink
                    >
                      <NotApplicable
                        {...this.props.EmployerNotApplicable}
                        name="EmployerNotApplicable"
                        attribute={`${attribute}.EmployerNotApplicable`}
                        label={i18n.t('relationships.relatives.label.idk')}
                        or={i18n.t('relationships.relatives.para.or')}
                        onError={this.props.onError}
                        onUpdate={this.updateEmployerNotApplicable}
                      >
                        <Text
                          attribute={`${attribute}.Employer`}
                          name="Employer"
                          className="relative-employer"
                          {...this.props.Employer}
                          onError={this.props.onError}
                          onUpdate={(value: RelationshipsRelativeItemModel['Employer']) => {
                            this.updateEmployer(value);
                          }}
                          required={this.props.required}
                        />
                      </NotApplicable>
                    </Field>

                    <Field
                      title={i18n.t('relationships.relatives.heading.employer.address')}
                      scrollIntoView={this.props.scrollIntoView}
                      attribute={`${attribute}.EmployerAddress`}
                      adjustFor="address"
                    >
                      <NotApplicable
                        {...this.props.EmployerAddressNotApplicable}
                        attribute={`${attribute}.EmployerAddressNotApplicable`}
                        name="EmployerAddressNotApplicable"
                        label={i18n.t('relationships.relatives.label.idk')}
                        or={i18n.t('relationships.relatives.para.or')}
                        onError={this.props.onError}
                        onUpdate={this.updateEmployerAddressNotApplicable}
                      >
                        <Location
                          attribute={`${attribute}.EmployerAddress`}
                          name="EmployerAddress"
                          {...this.props.EmployerAddress}
                          layout={Location.ADDRESS}
                          geocode
                          className="relative-employer-address"
                          showPostOffice
                          onError={this.props.onError}
                          onUpdate={(value: RelationshipsRelativeItemModel['EmployerAddress']) => {
                            this.updateEmployerAddress(value);
                          }}
                          required={this.props.required}
                        />
                      </NotApplicable>
                    </Field>
                    <Comments
                      title={i18n.t('relationships.comments.optional')}
                      name="EmployerComments"
                      className="optional-comment"
                      attribute="EmployerComments"
                      {...this.props.EmployerComments}
                      onUpdate={this.updateEmployerComments}
                      required={false}
                    />

                    <NotApplicable
                      {...this.props.EmployerRelationshipNotApplicable}
                      name="EmployerRelationshipNotApplicable"
                      attribute={`${attribute}.EmployerRelationshipNotApplicable`}
                      label={i18n.t('relationships.relatives.label.idk')}
                      or={i18n.t('relationships.relatives.para.or')}
                      onError={this.props.onError}
                      onUpdate={this.updateEmployerRelationshipNotApplicable}
                    >
                      <Branch
                        attribute={`${attribute}.HasAffiliation`}
                        name="has_affiliation"
                        label={i18n.t('relationships.relatives.heading.employer.affiliated')}
                        labelSize="h4"
                        className="relative-affiliation"
                        {...this.props.HasAffiliation}
                        onUpdate={(value: RelationshipsRelativeItemModel['HasAffiliation']) => {
                          this.updateField('HasAffiliation', value);
                        }}
                        required={this.props.required}
                        scrollIntoView={this.props.scrollIntoView}
                        onError={this.props.onError}
                      />
                      <Comments
                        title={i18n.t('relationships.comments.optional')}
                        name="HasAffiliationComments"
                        className="optional-comment"
                        attribute="HasAffiliationComments"
                        {...this.props.HasAffiliationComments}
                        onUpdate={this.updateHasAffiliationComments}
                        required={false}
                      />
                    </NotApplicable>
                    {requireRelationshipRelativesForeignGovtAffExplanation
                      && this.props.HasAffiliation.value === 'Yes'
                      && this.props.EmployerRelationshipNotApplicable?.applicable !== false
                      && (
                        <>
                          <Field
                            title={i18n.t('relationships.relatives.heading.employer.relationship')}
                            scrollIntoView={this.props.scrollIntoView}
                            attribute={`${attribute}.EmployerRelationship`}
                          >
                            <Textarea
                              attribute={`${attribute}.EmployerRelationship`}
                              name="EmployerRelationship"
                              className="relative-employer-relationship"
                              {...this.props.EmployerRelationship}
                              disabled={!this.props.EmployerRelationshipNotApplicable?.applicable}
                              onError={this.props.onError}
                              onUpdate={(value: RelationshipsRelativeItemModel['EmployerRelationship']) => {
                                this.updateEmployerRelationship(value);
                              }}
                              required={this.props.required}
                            />
                          </Field>
                          <Comments
                            title={i18n.t('relationships.comments.optional')}
                            name="ForeignAffiliationComments"
                            className="optional-comment"
                            attribute="ForeignAffiliationComments"
                            {...this.props.ForeignAffiliationComments}
                            onUpdate={this.updateForeignAffiliationComments}
                            required={false}
                          />
                        </>
                      )}
                  </div>
                )}
            </>
          )}
      </div>
    );
  }
}

Relative.defaultProps = {
  PvqRelation: '', // pvq only
  NoContactWithFamily: {}, // pvq only
  CitizenshipStatus: '', // pvq only
  PvqEmployerRelationship: {}, // pvq only
  PvqOtherGovtAgencyExplanation: {}, // pvq only
  Residency: '', // pvq only
  AlienNumber: {}, // pvq only
  CertificateNumber: {}, // pvq only
  InContact: {}, // pvq only
  BirthdateNotApplicable: { applicable: true }, // pvq only
  BirthplaceNotApplicable: { applicable: true }, // pvq only
  DeceasedNotApplicable: { applicable: true }, // pvq only
  RelativeRelationshipNotApplicable: { applicable: true }, // pvq only
  AffiliationNotApplicable: { applicable: true }, // pvq only
  Relation: '',
  Name: {},
  Birthdate: {},
  Birthplace: {},
  Citizenship: {},
  MaidenSameAsListed: '',
  MaidenName: {},
  Aliases: {},
  IsDeceased: {},
  Address: {},
  DocumentNumber: {},
  CertificateNumberNotApplicable: { applicable: true }, // pvq only
  CourtName: {},
  CourtNameNotApplicable: { applicable: true }, // pvq only
  CourtAddress: {},
  Document: '',
  DocumentComments: {},
  OptionalComments: {},
  ResidenceDocumentNumber: {},
  DocumentNumberNotApplicable: { applicable: true },
  DocumentNumberExplanation: {},
  Expiration: {},
  ExpirationNotApplicable: { applicable: true }, // pvq only
  FirstContact: {},
  LastContact: {},
  maritalStatus: {},
  Methods: {},
  MethodsComments: {},
  Frequency: '',
  FrequencyExplanation: {},
  FrequencyComments: {},
  AlternateAddress: {},
  AlternateAddressNotApplicable: { applicable: true },
  EmployerNotApplicable: { applicable: true },
  EmployerAddressNotApplicable: { applicable: true },
  EmployerRelationshipNotApplicable: { applicable: true },
  Employer: {},
  EmployerAddress: {},
  HasAffiliation: '',
  EmployerRelationship: {},
  addressBooks: {},
  addressBook: 'Relative',
  requireRelationshipRelativesForeignGovtAffExplanation: true,
  requireRelationshipRelativesUSResidenceDoc: true,
  EntryComments: {},
  EntryExplanation: {},
  ParentNotApplicable: { applicable: true },
  MothersMaidenNameComments: {},
  AliasesComments: {},
  AliasesSummaryComments: {},
  DeceasedComments: {},
  ForeignRelativeComments: {},
  EmployerComments: {},
  HasAffiliationComments: {},
  ForeignAffiliationComments: {},
  AnswerComments: {},
  AddressComments: {},
  PhysicalLocationComments: {},
  AddressNotApplicable: { applicable: true },
  AddressExplanation: {},
  CourtAddressNotApplicable: { applicable: true },
  CourtAddressExplanation: {},
  FirstContactNotApplicable: { applicable: true },
  FirstContactExplanation: {},
  InUsaNotApplicable: { applicable: true }, // pvq
  ParentCitizenshipStatusNotApplicable: { applicable: true }, // pvq
  HasRelativeAffiliations: {}, // pvq only
  NeverWorked: {}, // pvq only
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
